import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Divider,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Radio,
  Segmented,
  Select,
  Skeleton,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CloudDownloadOutlined,
  CloudUploadOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { AiSparkIcon } from '../../shared/components/AiSparkIcon.tsx';
import type {
  JiraSyncConfig,
  ItemTypeListItem,
  MappingTemplate,
  SpaceRole,
  FieldDefinition,
  PushPreviewResponse,
  PushPreviewField,
  JiraPullPreviewResponse,
  JiraPullExecuteResponse,
  JiraTransitionGraph,
} from '@lens/shared';
import { ApiError } from '../../shared/api/client.ts';
import { listItemTypes, getItemType } from '../item-types/api.ts';
import { AddFieldsFromJiraWizard } from '../item-types/AddFieldsFromJiraWizard.tsx';
import {
  listSyncConfigs,
  createSyncConfig,
  updateSyncConfig,
  deleteSyncConfig,
  copyMappingBetween,
  listMappingTemplates,
  listJiraProjects,
  listJiraIssueTypes,
  listJiraFields,
  fetchJiraProjectStatuses,
  suggestMapping,
  pushPreview,
  pullPreview,
  pullExecute,
  bulkJiraPush,
  getJob,
  syncLinksPush,
  fetchJiraTransitionGraph,
  scanJiraTransitionGraph,
  scanAllJiraTransitionGraph,
} from './api.ts';
import type { JiraProject, JiraIssueType, JiraFieldMeta, JiraProjectStatus } from './api.ts';
import { useJobs } from '../jobs/JobsContext.tsx';
import { TransitionGraphDiagram } from './TransitionGraphDiagram.tsx';
import { getDictionary } from '../dictionaries/api.ts';
import { queryItems } from '../items/api.ts';
import type { DictionaryColumn } from '@lens/shared';
import { radius, shadows, spacing } from '../../shared/theme/tokens.ts';
import './JiraSyncTab.css';

interface JiraSyncTabProps {
  spaceId: string;
  myRole?: SpaceRole;
}

interface FieldMappingRow {
  activitiesFieldSlug: string;
  jiraField: string;
  transform: 'none' | 'reference_key' | 'markdown_to_jira_wiki' | 'status_map' | 'bool_map';
  source: 'field' | 'fixed';
  fixedValue?: string;
  valueMap?: Record<string, string>;
  /** Колонка справочника для reference_key (пусто → ключевая колонка). */
  referenceColumnSlug?: string;
}

interface PullMappingRow {
  jiraField: string;
  activitiesFieldSlug: string;
  transform:
    | 'none'
    | 'reference_key_reverse'
    | 'jira_wiki_to_markdown'
    | 'jira_select_value'
    | 'status_map_reverse'
    | 'bool_map_reverse';
  valueMap?: Record<string, string>;
}

const PULL_TRANSFORM_OPTIONS = [
  { value: 'none', label: 'Без преобразования' },
  { value: 'reference_key_reverse', label: 'Справочник по ключу' },
  { value: 'jira_wiki_to_markdown', label: 'Markdown ↔ Wiki' },
  { value: 'jira_select_value', label: 'Select (значение)' },
  { value: 'status_map_reverse', label: 'Статус через workflow' },
  { value: 'bool_map_reverse', label: 'Да/Нет через значение' },
];

type PushTransform = 'none' | 'reference_key' | 'markdown_to_jira_wiki' | 'status_map' | 'bool_map';
type PullTransform = 'none' | 'reference_key_reverse' | 'jira_wiki_to_markdown' | 'jira_select_value' | 'status_map_reverse' | 'bool_map_reverse';

function convertPushTransformToPull(t: PushTransform): PullTransform {
  switch (t) {
    case 'reference_key': return 'reference_key_reverse';
    case 'markdown_to_jira_wiki': return 'jira_wiki_to_markdown';
    case 'status_map': return 'status_map_reverse';
    case 'bool_map': return 'bool_map_reverse';
    default: return 'none';
  }
}

function convertPullTransformToPush(t: PullTransform): PushTransform {
  switch (t) {
    case 'reference_key_reverse': return 'reference_key';
    case 'jira_wiki_to_markdown': return 'markdown_to_jira_wiki';
    case 'status_map_reverse': return 'status_map';
    case 'bool_map_reverse': return 'bool_map';
    default: return 'none';
  }
}

const WORKFLOW_STATUS_FIELD: JiraFieldMeta = {
  key: 'status',
  name: 'Статус (workflow)',
  required: false,
  schema: { type: 'status' },
};

const STATUS_CATEGORY_META: Record<string, { label: string; color: string }> = {
  backlog: { label: 'backlog', color: 'default' },
  active: { label: 'active', color: 'processing' },
  done: { label: 'done', color: 'success' },
  cancelled: { label: 'cancelled', color: 'error' },
};

function isLensStatusField(field: FieldDefinition | undefined): boolean {
  return field?.fieldType === 'select' && Boolean((field.config as { hasCategories?: boolean } | undefined)?.hasCategories);
}

function appendWorkflowStatusField(fields: JiraFieldMeta[]): JiraFieldMeta[] {
  return fields.some((field) => field.key === 'status')
    ? fields
    : [...fields, WORKFLOW_STATUS_FIELD];
}

// ─── Fuzzy auto-match (Lens fields ↔ Jira fields by name) ───────────────

const COMMON_ALIASES: Record<string, string[]> = {
  summary: ['title', 'name', 'название', 'заголовок', 'имя'],
  description: ['описание', 'details', 'body', 'содержание'],
  assignee: ['ответственный', 'исполнитель', 'владелец', 'owner', 'responsible'],
  reporter: ['автор', 'создатель', 'author'],
  priority: ['приоритет'],
  duedate: ['срок', 'дедлайн', 'deadline', 'due', 'date'],
  labels: ['метки', 'теги', 'tags'],
  components: ['компоненты', 'компонент', 'component'],
  fixversions: ['версия', 'релиз', 'release', 'version'],
  environment: ['среда', 'окружение'],
  status: ['статус', 'state'],
};

function normalizeForMatch(s: string): string {
  return s
    .toLowerCase()
    .replace(/\(.*?\)/g, '')
    .replace(/[_\-\s]/g, '')
    .replace(/[^a-zа-я0-9]/gi, '');
}

function fuzzyScore(a: string, b: string): number {
  if (!a || !b) return 0;
  const na = normalizeForMatch(a);
  const nb = normalizeForMatch(b);
  if (na === nb) return 1;
  if (na.includes(nb) || nb.includes(na)) return 0.8;
  for (const [canon, aliases] of Object.entries(COMMON_ALIASES)) {
    const group = [canon, ...aliases].map(normalizeForMatch);
    if (group.includes(na) && group.includes(nb)) return 0.9;
    if (group.includes(na) && group.some((g) => nb.includes(g) || g.includes(nb))) return 0.7;
    if (group.includes(nb) && group.some((g) => na.includes(g) || g.includes(na))) return 0.7;
  }
  return 0;
}

interface AutoMatchResult {
  rows: FieldMappingRow[];
  confident: number;
  guessed: number;
  unmatched: string[];
}

const EXCLUDED_JIRA_FIELDS = new Set(['project', 'issuetype', 'summary']);

function autoMatchByName(
  lensFields: FieldDefinition[],
  jiraFields: JiraFieldMeta[],
  summarySourceSlug: string,
): AutoMatchResult {
  const excluded = EXCLUDED_JIRA_FIELDS;
  const usedLensSlugs = new Set<string>([summarySourceSlug]);
  const rows: FieldMappingRow[] = [];
  let confident = 0;
  let guessed = 0;
  const unmatched: string[] = [];

  for (const jira of jiraFields) {
    if (excluded.has(jira.key)) continue;

    let best: { field: FieldDefinition; score: number } | null = null;
    for (const lens of lensFields) {
      if (usedLensSlugs.has(lens.slug)) continue;
      const score = Math.max(
        fuzzyScore(jira.key, lens.slug),
        fuzzyScore(jira.key, lens.name),
        fuzzyScore(jira.name, lens.slug),
        fuzzyScore(jira.name, lens.name),
      );
      if (jira.schema.type === 'status' && !isLensStatusField(lens)) {
        continue;
      }
      if (!best || score > best.score) {
        best = { field: lens, score };
      }
    }

    if (best && best.score >= 0.7) {
      usedLensSlugs.add(best.field.slug);
      if (best.score >= 0.9) confident++;
      else guessed++;
      rows.push({
        activitiesFieldSlug: best.field.slug,
        jiraField: jira.key,
        transform: jira.schema.type === 'status'
          ? 'status_map'
          : best.field.fieldType === 'reference'
          ? 'reference_key'
          : (best.field.fieldType === 'markdown' || best.field.fieldType === 'textarea')
            ? 'none'
            : 'none',
        source: 'field',
      });
    } else if (jira.required) {
      rows.push({ activitiesFieldSlug: '', jiraField: jira.key, transform: 'none', source: 'field' });
      unmatched.push(`${jira.name} (обязательное)`);
    }
  }

  return { rows, confident, guessed, unmatched };
}

function buildPushMappingsFromSuggestions(
  suggestions: Array<{ lensSlug: string; jiraKey: string; transform: string }>,
): FieldMappingRow[] {
  return suggestions.map((suggestion) => ({
    activitiesFieldSlug: suggestion.lensSlug,
    jiraField: suggestion.jiraKey,
    transform: suggestion.jiraKey === 'status'
      ? 'status_map'
      : (suggestion.transform || 'none') as FieldMappingRow['transform'],
    source: 'field',
  }));
}

const PULL_MODE_OPTIONS = [
  { value: 'create_only', label: 'Только создание' },
  { value: 'create_and_update', label: 'Создание и обновление' },
];

/** Двухстрочный пункт меню «Заполнить автоматически»: название + пояснение. */
function autoFillItem(
  key: string,
  icon: React.ReactNode,
  title: string,
  desc: string,
  opts: { disabled?: boolean; onClick?: () => void } = {},
) {
  const disabled = !!opts.disabled;
  return {
    key,
    disabled,
    onClick: opts.onClick,
    label: (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '3px 2px' }}>
        <span style={{ marginTop: 2, fontSize: 15, lineHeight: 1, color: disabled ? 'rgba(0,0,0,0.25)' : 'var(--color-primary)' }}>{icon}</span>
        <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
          <span style={{ fontWeight: 500, color: disabled ? 'rgba(0,0,0,0.25)' : 'var(--color-text)' }}>{title}</span>
          <span style={{ fontSize: 12, color: disabled ? 'rgba(0,0,0,0.25)' : 'var(--color-text-secondary)', fontWeight: 400 }}>{desc}</span>
        </span>
      </div>
    ),
  };
}

interface ConfigFormValues {
  itemTypeId: string;
  jiraProjectKey: string;
  jiraIssueType: string;
  summarySource: string;
  syncDirection: 'push_only' | 'pull_only' | 'both';
  templateId?: string;
}

type ValidationStatus = 'ok' | 'warning' | 'error';

interface MappingValidation {
  status: ValidationStatus;
  code: string;
  message: string;
}

const TRANSFORM_OPTIONS = [
  { value: 'none', label: 'Без преобразования' },
  { value: 'reference_key', label: 'Справочник по ключу' },
  { value: 'markdown_to_jira_wiki', label: 'Markdown ↔ Wiki' },
];

// ─── Inline validation (runs on every change, no button needed) ────────

function validateStatusValueMap(
  lensField: FieldDefinition,
  valueMap: Record<string, string> | undefined,
  jiraStatuses: JiraProjectStatus[],
): MappingValidation | null {
  const options = ((lensField.config as { options?: Array<{ value: string }> }).options ?? []);
  const optionValues = new Set(options.map((option) => option.value));
  const jiraStatusNames = new Set(jiraStatuses.map((status) => status.name));

  if (!valueMap || Object.keys(valueMap).length === 0) {
    return { status: 'warning', code: 'value_map_empty', message: 'Сопоставление статусов не задано' };
  }

  for (const lensOptionValue of Object.keys(valueMap)) {
    if (!optionValues.has(lensOptionValue)) {
      return { status: 'error', code: 'lens_option_deleted', message: `Опция Lens «${lensOptionValue}» больше не существует` };
    }
  }

  if (jiraStatuses.length > 0) {
    for (const jiraStatusName of Object.values(valueMap)) {
      if (!jiraStatusNames.has(jiraStatusName)) {
        return { status: 'error', code: 'jira_status_deleted', message: `Jira-статус «${jiraStatusName}» больше недоступен в проекте` };
      }
    }
  }

  return null;
}

export function validateRowInline(
  row: FieldMappingRow | PullMappingRow,
  selectedTypeFields: FieldDefinition[],
  jiraFields: JiraFieldMeta[],
  jiraStatuses: JiraProjectStatus[] = [],
): MappingValidation | null {
  const fieldBySlug = new Map(selectedTypeFields.map((f) => [f.slug, f]));
  const jiraFieldByKey = new Map(jiraFields.map((f) => [f.key, f]));
  const isPushRow = 'source' in row;
  const isStatusTransform = row.transform === 'status_map' || row.transform === 'status_map_reverse';

  if (!row.jiraField) {
    return { status: 'warning', code: 'jira_field_not_found', message: 'Поле Jira не выбрано' };
  }

  if (isPushRow && row.source === 'fixed' && !row.fixedValue?.trim()) {
    return { status: 'warning', code: 'empty_source_value', message: 'Фиксированное значение пустое' };
  }

  if ((!isPushRow || row.source === 'field') && !row.activitiesFieldSlug) {
    return { status: 'warning', code: 'empty_source_value', message: 'Поле Lens не выбрано' };
  }

  const jiraMeta = jiraFieldByKey.get(row.jiraField);
  if (jiraFields.length > 0 && !jiraMeta) {
    return { status: 'error', code: 'jira_field_not_found', message: `Jira-поле «${row.jiraField}» недоступно` };
  }

  if (isPushRow && row.source === 'fixed') {
    return { status: 'ok', code: 'ok', message: 'OK' };
  }

  const lensField = fieldBySlug.get(row.activitiesFieldSlug);
  if (!lensField) {
    return { status: 'error', code: 'lens_field_deleted', message: `Поле «${row.activitiesFieldSlug}» не найдено` };
  }

  if (row.transform === 'reference_key' && lensField.fieldType !== 'reference') {
    return { status: 'error', code: 'type_mismatch', message: 'reference_key только для reference' };
  }

  if (row.transform === 'markdown_to_jira_wiki' && lensField.fieldType !== 'markdown' && lensField.fieldType !== 'textarea') {
    return { status: 'error', code: 'type_mismatch', message: 'markdown_to_jira_wiki только для markdown/textarea' };
  }

  const isBoolTransform = row.transform === 'bool_map' || row.transform === 'bool_map_reverse';
  if (isBoolTransform) {
    if (lensField.fieldType !== 'boolean') {
      return { status: 'error', code: 'type_mismatch', message: 'Сопоставление Да/Нет только для boolean-поля' };
    }
    const hasMap = row.valueMap && (row.valueMap.true?.trim() || row.valueMap.false?.trim());
    if (!hasMap) {
      return { status: 'warning', code: 'empty_source_value', message: 'Задайте значения для «Да»/«Нет»' };
    }
    return { status: 'ok', code: 'ok', message: 'OK' };
  }

  if (jiraMeta && !isFieldTypeCompatible(lensField, jiraMeta)) {
    return { status: 'error', code: 'type_mismatch', message: `Тип «${lensField.fieldType}» не совместим с Jira-полем` };
  }

  if (isStatusTransform) {
    const statusValidation = validateStatusValueMap(lensField, row.valueMap, jiraStatuses);
    if (statusValidation) return statusValidation;
  }

  return { status: 'ok', code: 'ok', message: 'OK' };
}

/**
 * Сентинел fixedValue для резолва на бэке в логин инициатора push'а.
 * Должен совпадать с CURRENT_USER_LOGIN_TOKEN в apps/api/src/modules/jira/mapping.service.ts.
 */
const CURRENT_USER_LOGIN_TOKEN = '@me';

const USER_SHAPED_SYSTEM_FIELDS = new Set(['assignee', 'reporter', 'creator']);

/** True, если Jira-поле принимает пользователя (login → `{ name }`). */
function isUserShapedJiraField(jiraField: string, meta: JiraFieldMeta | undefined): boolean {
  if (USER_SHAPED_SYSTEM_FIELDS.has(jiraField)) return true;
  if (!meta) return false;
  const type = meta.schema?.type?.toLowerCase();
  const items = meta.schema?.items?.toLowerCase();
  const custom = meta.schema?.custom?.toLowerCase() ?? '';
  if (type === 'user') return true;
  if (type === 'array' && items === 'user') return true;
  if (custom.includes('userpicker')) return true;
  return false;
}

export function isFieldTypeCompatible(lensField: FieldDefinition, jiraField: JiraFieldMeta): boolean {
  if (jiraField.schema.type.toLowerCase() === 'status') {
    return isLensStatusField(lensField);
  }

  const fieldType = lensField.fieldType;
  const type = jiraField.schema.type.toLowerCase();
  const items = (jiraField.schema.items ?? '').toLowerCase();
  const isOption = type === 'option';
  const isArray = type === 'array';

  switch (fieldType) {
    case 'text': case 'textarea': case 'markdown': case 'url': case 'computed':
      return type === 'string' || type === 'any' || isOption;
    case 'number':
      return type === 'number' || type === 'string';
    case 'date':
      return type === 'date' || type === 'datetime' || type === 'string';
    case 'select':
      return isOption || type === 'string';
    case 'multi_select':
      return isArray || type === 'string' || items === 'option';
    case 'reference':
      // reference хранит ключ из словаря (строка) → совместим с любым нестатусным Jira-полем;
      // статус-поля отсечены в начале функции через isLensStatusField.
      return true;
    case 'boolean':
      return type === 'boolean' || type === 'string';
    default:
      return true;
  }
}

// ─── Status icon helper ───────────────────────────────────────────────

function StatusIcon({ status }: { status: ValidationStatus }) {
  if (status === 'ok') return <CheckCircleOutlined style={{ color: 'var(--color-success)', fontSize: 14 }} />;
  if (status === 'warning') return <WarningOutlined style={{ color: 'var(--color-warning)', fontSize: 14 }} />;
  return <CloseCircleOutlined style={{ color: 'var(--color-error)', fontSize: 14 }} />;
}

// ─── Main component ───────────────────────────────────────────────────

export function JiraSyncTab({ spaceId, myRole }: JiraSyncTabProps): JSX.Element {
  const [configs, setConfigs] = useState<JiraSyncConfig[]>([]);
  const [itemTypes, setItemTypes] = useState<ItemTypeListItem[]>([]);
  const [templates, setTemplates] = useState<MappingTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<JiraSyncConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const { registerJob } = useJobs();
  const [form] = Form.useForm<ConfigFormValues>();
  const watchedSummarySource = Form.useWatch('summarySource', form);

  // Mapping rows (push)
  const [fieldMappings, setFieldMappings] = useState<FieldMappingRow[]>([]);
  // Колонки целевых справочников reference-полей (для выбора колонки в reference_key).
  // Ключ — dictionaryId, значение — колонки справочника.
  const [refDictColumns, setRefDictColumns] = useState<Record<string, DictionaryColumn[]>>({});
  // Имя ключевой колонки справочника по dictionaryId — чтобы подписать
  // reference-колонку «↗ … → <ключ>» (логин/код/название, в зависимости от справочника).
  const [dictKeyName, setDictKeyName] = useState<Record<string, string>>({});
  // Mapping rows (pull)
  const [pullMappings, setPullMappings] = useState<PullMappingRow[]>([]);
  const [pullSummaryTarget, setPullSummaryTarget] = useState<string>('title');

  // Pull preview/execute state
  const [pullPreviewOpen, setPullPreviewOpen] = useState(false);
  const [pullPreviewData, setPullPreviewData] = useState<JiraPullPreviewResponse | null>(null);
  const [pullPreviewLoading, setPullPreviewLoading] = useState(false);
  const [pullExecuteLoading, setPullExecuteLoading] = useState(false);
  const [pullExecuteResult, setPullExecuteResult] = useState<JiraPullExecuteResponse | null>(null);

  // Push execute (bulk) state
  const [pushExecuteLoading, setPushExecuteLoading] = useState<string | null>(null); // itemTypeId
  const [pushLinksRunning, setPushLinksRunning] = useState(false);

  // ─── Run-sync modal (запуск синхронизации, отдельно от настройки маппинга) ──
  // Открывается из строки списка конфигов. Параметры запуска (JQL/режим/max/
  // summary) живут здесь и не персистятся — это разовые опции прогона.
  const [runConfig, setRunConfig] = useState<JiraSyncConfig | null>(null);
  const [runJql, setRunJql] = useState<string>('');
  const [runMode, setRunMode] = useState<'create_only' | 'create_and_update'>('create_only');
  const [runMaxResults, setRunMaxResults] = useState<number>(100);
  const [runSummaryTarget, setRunSummaryTarget] = useState<string>('title');
  // Поля типа записи для выбора summary в окне запуска (подгружаются на открытие).
  const [runFields, setRunFields] = useState<FieldDefinition[]>([]);

  // Copy-mapping dialog
  const [copyModalOpen, setCopyModalOpen] = useState<null | { direction: 'push' | 'pull' | 'both' }>(null);
  const [copySourceItemTypeId, setCopySourceItemTypeId] = useState<string | null>(null);
  /** Из какого направления исходного itemType брать маппинг ('push' | 'pull'). По умолчанию совпадает с целевым. */
  const [copySourceDirection, setCopySourceDirection] = useState<'push' | 'pull'>('push');

  // Active tab in edit modal
  const [mappingTab, setMappingTab] = useState<'push' | 'pull'>('push');

  // Lens fields for selected item type
  const [selectedTypeFields, setSelectedTypeFields] = useState<FieldDefinition[]>([]);
  const [addFieldsFromJiraOpen, setAddFieldsFromJiraOpen] = useState(false);
  const [fieldsLoading, setFieldsLoading] = useState(false);

  // Источник Jira projectKey: static (из конфига) или field (из поля записи).
  const [projectKeySourceKind, setProjectKeySourceKind] = useState<'static' | 'field'>('static');
  const [projectKeyFieldId, setProjectKeyFieldId] = useState<string | null>(null);
  // valueMap для select-поля projectKey: option.value → projectKey
  const [projectKeyValueMap, setProjectKeyValueMap] = useState<Record<string, string>>({});

  // Источник Jira issueType: static (из конфига) или field (из select-поля записи).
  const [issueTypeSourceKind, setIssueTypeSourceKind] = useState<'static' | 'field'>('static');
  const [issueTypeFieldId, setIssueTypeFieldId] = useState<string | null>(null);
  // valueMap: option.value → имя issue type'а в Jira
  const [issueTypeValueMap, setIssueTypeValueMap] = useState<Record<string, string>>({});

  // Jira proxy data
  const [jiraProjects, setJiraProjects] = useState<JiraProject[]>([]);
  const [jiraIssueTypes, setJiraIssueTypes] = useState<JiraIssueType[]>([]);
  const [jiraFields, setJiraFields] = useState<JiraFieldMeta[]>([]);
  const [jiraStatuses, setJiraStatuses] = useState<JiraProjectStatus[]>([]);
  const [jiraAvailable, setJiraAvailable] = useState(true);
  const [jiraProjectsLoading, setJiraProjectsLoading] = useState(false);
  const [jiraIssueTypesLoading, setJiraIssueTypesLoading] = useState(false);
  const [jiraFieldsLoading, setJiraFieldsLoading] = useState(false);
  const [jiraStatusesLoading, setJiraStatusesLoading] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [statusMappingEditor, setStatusMappingEditor] = useState<null | {
    mode: 'push' | 'pull';
    index: number;
    valueMap: Record<string, string>;
  }>(null);

  // Диаграмма workflow-переходов в модалке маппинга статусов (push, сохранённый конфиг).
  const [transitionGraph, setTransitionGraph] = useState<JiraTransitionGraph | null>(null);
  const [transitionGraphLoading, setTransitionGraphLoading] = useState(false);
  const [transitionScanKeys, setTransitionScanKeys] = useState('');
  const [transitionScanLoading, setTransitionScanLoading] = useState(false);
  const [transitionScanAllLoading, setTransitionScanAllLoading] = useState(false);

  // Сопоставление boolean → значение Jira-опции (Да/Нет → «Штат»/«Вендор»).
  const [boolMappingEditor, setBoolMappingEditor] = useState<null | {
    mode: 'push' | 'pull';
    index: number;
    valueMap: Record<string, string>;
  }>(null);

  // Preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<PushPreviewResponse | null>(null);

  const isAdmin = myRole === 'admin';

  // ─── Computed inline validations (reactive, no button) ──────────────

  const rowValidations = useMemo(() => {
    const result: Record<number, MappingValidation> = {};
    fieldMappings.forEach((row, index) => {
      const v = validateRowInline(row, selectedTypeFields, jiraFields, jiraStatuses);
      if (v) result[index] = v;
    });
    return result;
  }, [fieldMappings, selectedTypeFields, jiraFields, jiraStatuses]);

  // Догружаем колонки целевых справочников для reference-полей текущего типа —
  // чтобы в reference_key можно было выбрать колонку (в т.ч. колонку-ссылку «Тех. лид»).
  useEffect(() => {
    const dictIds = new Set<string>();
    for (const fd of selectedTypeFields) {
      if (fd.fieldType !== 'reference') continue;
      const dictId = (fd.config as { dictionaryId?: string } | undefined)?.dictionaryId;
      if (dictId && !refDictColumns[dictId]) dictIds.add(dictId);
    }
    if (dictIds.size === 0) return;
    let cancelled = false;
    void (async () => {
      const loaded: Record<string, DictionaryColumn[]> = {};
      const keyNames: Record<string, string> = {};
      const keyNameOf = (dict: Awaited<ReturnType<typeof getDictionary>>): string | undefined =>
        (dict.columns ?? []).find((c) => c.id === dict.keyColumnId)?.name;

      for (const dictId of dictIds) {
        try {
          const dict = await getDictionary(dictId);
          loaded[dictId] = dict.columns ?? [];
          const kn = keyNameOf(dict);
          if (kn) keyNames[dictId] = kn;
          // Для reference-колонок этого справочника подтягиваем имя ключевой
          // колонки их целевого справочника (чтобы подпись была точной).
          for (const col of dict.columns ?? []) {
            const targetId = (col.config as { dictionaryId?: string } | undefined)?.dictionaryId;
            if (col.columnType === 'reference' && targetId && !keyNames[targetId]) {
              try {
                const target = await getDictionary(targetId);
                const tkn = keyNameOf(target);
                if (tkn) keyNames[targetId] = tkn;
              } catch {
                /* целевой справочник недоступен — подпись откатится на «ключ» */
              }
            }
          }
        } catch {
          /* справочник недоступен — пропускаем, останется только «ключевая колонка» */
        }
      }
      if (!cancelled) {
        if (Object.keys(loaded).length > 0) setRefDictColumns((prev) => ({ ...prev, ...loaded }));
        if (Object.keys(keyNames).length > 0) setDictKeyName((prev) => ({ ...prev, ...keyNames }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTypeFields, refDictColumns]);

  const pullRowValidations = useMemo(() => {
    const result: Record<number, MappingValidation> = {};
    pullMappings.forEach((row, index) => {
      const v = validateRowInline(row, selectedTypeFields, jiraFields, jiraStatuses);
      if (v) result[index] = v;
    });
    return result;
  }, [pullMappings, selectedTypeFields, jiraFields, jiraStatuses]);

  const globalErrors = useMemo(() => {
    const errs: string[] = [];
    const summarySource = form.getFieldValue('summarySource') as string | undefined;
    if (summarySource && selectedTypeFields.length > 0 && !selectedTypeFields.find((f) => f.slug === summarySource)) {
      errs.push('summarySource ссылается на несуществующее поле.');
    }

    const requiredJiraFields = jiraFields.filter((f) => f.required && !EXCLUDED_JIRA_FIELDS.has(f.key));
    const filledJiraKeys = new Set(fieldMappings.filter((m) => m.jiraField).map((m) => m.jiraField));

    for (const rf of requiredJiraFields) {
      if (!filledJiraKeys.has(rf.key)) {
        errs.push(`Обязательное Jira-поле «${rf.name}» (${rf.key}) не замаплено — push может упасть.`);
      }
    }

    return errs;
  }, [selectedTypeFields, jiraFields, fieldMappings, form]);

  const pushHasErrors = useMemo(
    () => Object.values(rowValidations).some((v) => v.status === 'error'),
    [rowValidations],
  );
  const pullHasErrors = useMemo(
    () => Object.values(pullRowValidations).some((v) => v.status === 'error'),
    [pullRowValidations],
  );
  const hasErrors = pushHasErrors || pullHasErrors;

  // ─── Data loading ───────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cfgs, types, tmpls] = await Promise.all([
        listSyncConfigs(spaceId),
        listItemTypes(spaceId),
        listMappingTemplates(),
      ]);
      setConfigs(cfgs);
      setItemTypes(types);
      setTemplates(tmpls);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  }, [spaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadJiraProjects = async () => {
    setJiraProjectsLoading(true);
    try {
      const projects = await listJiraProjects();
      setJiraProjects(projects);
      setJiraAvailable(true);
    } catch {
      setJiraProjects([]);
      setJiraAvailable(false);
    } finally {
      setJiraProjectsLoading(false);
    }
  };

  const loadJiraIssueTypes = async (projectKey: string) => {
    setJiraIssueTypesLoading(true);
    setJiraIssueTypes([]);
    setJiraFields([]);
    try {
      const types = await listJiraIssueTypes(projectKey);
      setJiraIssueTypes(types);
      return types;
    } catch {
      setJiraIssueTypes([]);
      return [];
    } finally {
      setJiraIssueTypesLoading(false);
    }
  };

  const loadJiraStatuses = async (projectKey: string) => {
    setJiraStatusesLoading(true);
    try {
      const statuses = await fetchJiraProjectStatuses(projectKey);
      setJiraStatuses(statuses);
      return statuses;
    } catch {
      setJiraStatuses([]);
      return [];
    } finally {
      setJiraStatusesLoading(false);
    }
  };

  const loadJiraFieldsMeta = async (projectKey: string, issueTypeId: string): Promise<JiraFieldMeta[]> => {
    setJiraFieldsLoading(true);
    try {
      const fields = appendWorkflowStatusField(await listJiraFields(projectKey, issueTypeId));
      setJiraFields(fields);
      return fields;
    } catch {
      setJiraFields([]);
      return [];
    } finally {
      setJiraFieldsLoading(false);
    }
  };

  const mappingPrereqMissing = (() => {
    const missing: string[] = [];
    if (selectedTypeFields.length === 0) missing.push('тип записи');
    if (jiraFields.length === 0) missing.push('Jira-проект и тип задачи');
    if (missing.length === 0) return null;
    return `Сначала выберите ${missing.join(' и ')}`;
  })();

  const configuredItemTypeIds = new Set(configs.map((c) => c.itemTypeId));
  const availableItemTypes = itemTypes.filter(
    (t) => !configuredItemTypeIds.has(t.id) || t.id === editingConfig?.itemTypeId,
  );

  const loadTypeFields = async (itemTypeId: string) => {
    setFieldsLoading(true);
    try {
      const itemType = await getItemType(spaceId, itemTypeId);
      setSelectedTypeFields(itemType.fields);
    } catch {
      setSelectedTypeFields([]);
    } finally {
      setFieldsLoading(false);
    }
  };

  // ─── Handlers ───────────────────────────────────────────────────────

  const handleAdd = () => {
    setEditingConfig(null);
    setFieldMappings([]);
    setPullMappings([]);
    setPullSummaryTarget('title');
    setMappingTab('push');
    setSelectedTypeFields([]);
    setJiraIssueTypes([]);
    setJiraFields([]);
    setJiraStatuses([]);
    setProjectKeySourceKind('static');
    setProjectKeyFieldId(null);
    setProjectKeyValueMap({});
    setIssueTypeSourceKind('static');
    setIssueTypeFieldId(null);
    setIssueTypeValueMap({});
    form.resetFields();
    form.setFieldsValue({ summarySource: 'title', syncDirection: 'both' });
    setModalOpen(true);
    void loadJiraProjects();
  };

  const handleEdit = (config: JiraSyncConfig) => {
    setEditingConfig(config);
    setMappingTab('push');
    setFieldMappings(
      config.fieldMapping.fields.map((f) => ({
        activitiesFieldSlug: f.activitiesFieldSlug,
        jiraField: f.jiraField,
        transform: (f.transform ?? 'none') as FieldMappingRow['transform'],
        source: (f.source ?? 'field') as 'field' | 'fixed',
        fixedValue: f.fixedValue,
        valueMap: f.valueMap,
        referenceColumnSlug: (f as { referenceColumnSlug?: string }).referenceColumnSlug,
      })),
    );
    const pm = (config as any).pullFieldMapping as
      | { summaryTarget: string; fields: PullMappingRow[] }
      | null
      | undefined;
    setPullMappings(pm && Array.isArray(pm.fields) ? pm.fields.map((r) => ({ ...r })) : []);
    setPullSummaryTarget(config.fieldMapping.summarySource || 'title');
    // projectKeySource / issueTypeSource из settings (default: static)
    const settingsRaw = (config as any).settings as Record<string, unknown> | undefined;
    const pkSource = (settingsRaw?.projectKeySource ?? null) as
      | { kind: 'static' }
      | { kind: 'field'; fieldDefinitionId: string; valueMap?: Record<string, string> }
      | null;
    if (pkSource && pkSource.kind === 'field') {
      setProjectKeySourceKind('field');
      setProjectKeyFieldId(pkSource.fieldDefinitionId);
      setProjectKeyValueMap(pkSource.valueMap ?? {});
    } else {
      setProjectKeySourceKind('static');
      setProjectKeyFieldId(null);
      setProjectKeyValueMap({});
    }
    const itSource = (settingsRaw?.issueTypeSource ?? null) as
      | { kind: 'static' }
      | { kind: 'field'; fieldDefinitionId: string; valueMap?: Record<string, string> }
      | null;
    if (itSource && itSource.kind === 'field') {
      setIssueTypeSourceKind('field');
      setIssueTypeFieldId(itSource.fieldDefinitionId);
      setIssueTypeValueMap(itSource.valueMap ?? {});
    } else {
      setIssueTypeSourceKind('static');
      setIssueTypeFieldId(null);
      setIssueTypeValueMap({});
    }
    form.setFieldsValue({
      itemTypeId: config.itemTypeId,
      jiraProjectKey: config.jiraProjectKey,
      jiraIssueType: config.jiraIssueType,
      summarySource: config.fieldMapping.summarySource,
      syncDirection: (config as any).syncDirection ?? 'both',
    });
    void loadTypeFields(config.itemTypeId);
    setModalOpen(true);
    void loadJiraProjects().then(() => {
      void loadJiraStatuses(config.jiraProjectKey);
      void loadJiraIssueTypes(config.jiraProjectKey).then((types) => {
        const normalize = (s: string) => s.normalize('NFC').trim().toLowerCase();
        const issueType =
          types.find((t) => normalize(t.name) === normalize(config.jiraIssueType)) ??
          types.find((t) => !t.subtask);
        if (issueType) {
          void loadJiraFieldsMeta(config.jiraProjectKey, issueType.id);
        }
      });
    });
  };

  const handleDelete = (config: JiraSyncConfig) => {
    const typeName = itemTypes.find((t) => t.id === config.itemTypeId)?.name ?? config.itemTypeId;
    Modal.confirm({
      title: 'Удалить Jira-маппинг?',
      content: `Маппинг для типа «${typeName}» будет удалён. Уже синхронизированные записи сохранят свои Jira-ключи.`,
      okText: 'Удалить',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await deleteSyncConfig(spaceId, config.itemTypeId);
          setConfigs((prev) => prev.filter((c) => c.id !== config.id));
        } catch (err) {
          setError(err instanceof ApiError ? err.message : 'Не удалось удалить');
        }
      },
    });
  };

  const applyTemplateRows = (tpl: MappingTemplate, mode: 'replace' | 'merge') => {
    const tplRows = (tpl.fieldMapping.fields ?? []).map((f) => ({
      activitiesFieldSlug: f.activitiesFieldSlug,
      jiraField: f.jiraField,
      transform: (f.transform ?? 'none') as FieldMappingRow['transform'],
      source: (f.source ?? 'field') as 'field' | 'fixed',
      fixedValue: f.fixedValue,
      valueMap: f.valueMap,
      referenceColumnSlug: (f as { referenceColumnSlug?: string }).referenceColumnSlug,
    }));
    form.setFieldsValue({
      jiraProjectKey: tpl.jiraProjectPattern ?? form.getFieldValue('jiraProjectKey'),
      jiraIssueType: tpl.jiraIssueType ?? form.getFieldValue('jiraIssueType'),
      summarySource: tpl.fieldMapping.summarySource,
    });
    if (mode === 'replace') {
      setFieldMappings(tplRows);
    } else {
      setFieldMappings((prev) => {
        const existing = new Set(prev.map((r) => r.jiraField));
        const additions = tplRows.filter((r) => !existing.has(r.jiraField));
        return [...prev, ...additions];
      });
    }
    if (tpl.jiraProjectPattern) {
      void loadJiraIssueTypes(tpl.jiraProjectPattern);
    }
  };

  const handleApplyTemplate = (templateId: string) => {
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl) return;
    const currentKeys = new Set(fieldMappings.map((r) => r.jiraField).filter(Boolean));
    const tplKeys = new Set(tpl.fieldMapping.fields.map((r) => r.jiraField));
    const additions = [...tplKeys].filter((k) => !currentKeys.has(k));
    const overlaps = [...tplKeys].filter((k) => currentKeys.has(k));

    if (fieldMappings.length === 0) {
      applyTemplateRows(tpl, 'replace');
      void message.success(`Шаблон «${tpl.name}» применён (${tpl.fieldMapping.fields.length} строк)`);
      return;
    }

    Modal.confirm({
      title: `Применить шаблон «${tpl.name}»?`,
      width: 480,
      content: (
        <div>
          <Typography.Paragraph style={{ marginBottom: 8 }}>
            В текущем маппинге {fieldMappings.length} строк, в шаблоне — {tpl.fieldMapping.fields.length}.
          </Typography.Paragraph>
          <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
            <li>Добавить новых: <b>{additions.length}</b> ({additions.slice(0, 5).join(', ')}{additions.length > 5 ? '…' : ''})</li>
            <li>Перекрывающихся полей Jira: <b>{overlaps.length}</b> ({overlaps.slice(0, 5).join(', ')}{overlaps.length > 5 ? '…' : ''})</li>
          </ul>
        </div>
      ),
      okText: 'Дополнить (оставить текущие)',
      cancelText: 'Отмена',
      okButtonProps: { type: 'default' },
      onOk: () => {
        applyTemplateRows(tpl, 'merge');
        void message.success(`Добавлено ${additions.length} строк из шаблона`);
      },
      footer: (_, { OkBtn, CancelBtn }) => (
        <Space>
          <CancelBtn />
          <Button
            danger
            onClick={() => {
              Modal.destroyAll();
              applyTemplateRows(tpl, 'replace');
              void message.success(`Шаблон «${tpl.name}» заменил текущий маппинг`);
            }}
          >
            Заменить полностью
          </Button>
          <OkBtn />
        </Space>
      ),
    });
  };

  const handleProjectChange = (projectKey: string) => {
    form.setFieldsValue({ jiraIssueType: undefined as any });
    setJiraFields([]);
    setJiraStatuses([]);
    void loadJiraStatuses(projectKey);
    void loadJiraIssueTypes(projectKey);
  };

  const handleIssueTypeChange = (issueTypeName: string) => {
    const projectKey = form.getFieldValue('jiraProjectKey') as string;
    if (!projectKey) return;
    const issueType = jiraIssueTypes.find((t) => t.name === issueTypeName);
    if (!issueType) return;
    void loadJiraFieldsMeta(projectKey, issueType.id).then((fields) => {
      const summarySlug = (form.getFieldValue('summarySource') as string) || 'title';
      if (fieldMappings.length === 0 && selectedTypeFields.length > 0) {
        const matched = autoMatchByName(selectedTypeFields, fields, summarySlug);
        if (matched.rows.length > 0) {
          setFieldMappings(matched.rows);
          void message.success(
            `Авто-сопоставлено: уверенно ${matched.confident}, по похожему имени ${matched.guessed}`
            + (matched.unmatched.length ? `. Без пары: ${matched.unmatched.join(', ')}` : ''),
          );
          return;
        }
      }
      const requiredFields = fields.filter((f) => f.required && !EXCLUDED_JIRA_FIELDS.has(f.key));
      if (requiredFields.length === 0) return;
      setFieldMappings((prev) => {
        const existingJiraKeys = new Set(prev.map((r) => r.jiraField));
        const newRows = requiredFields
          .filter((f) => !existingJiraKeys.has(f.key))
          .map((f) => ({
            activitiesFieldSlug: '',
            jiraField: f.key,
            transform: 'none' as const,
            source: 'field' as const,
          }));
        return newRows.length > 0 ? [...prev, ...newRows] : prev;
      });
    });
  };

  const handleAutoMatch = () => {
    if (selectedTypeFields.length === 0 || jiraFields.length === 0) {
      void message.warning('Сначала выберите тип записи, проект и тип задачи Jira');
      return;
    }
    const summarySlug = (form.getFieldValue('summarySource') as string) || 'title';
    const result = autoMatchByName(selectedTypeFields, jiraFields, summarySlug);
    setFieldMappings(result.rows);
    void message.success(
      `Авто-сопоставлено: уверенно ${result.confident}, по похожему ${result.guessed}`
      + (result.unmatched.length ? `. Без пары: ${result.unmatched.join(', ')}` : ''),
    );
  };

  const handleItemTypeChange = (itemTypeId: string) => {
    void loadTypeFields(itemTypeId);
  };

  const addFieldMapping = () => {
    setFieldMappings((prev) => [...prev, { activitiesFieldSlug: '', jiraField: '', transform: 'none', source: 'field', valueMap: {} }]);
  };

  const removeFieldMapping = (index: number) => {
    setFieldMappings((prev) => prev.filter((_, i) => i !== index));
  };

  const updateFieldMapping = (index: number, field: Partial<FieldMappingRow>) => {
    setFieldMappings((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, ...field };
        const jiraMeta = jiraFields.find((jiraField) => jiraField.key === next.jiraField);

        if (field.jiraField === 'status' || jiraMeta?.schema.type === 'status') {
          next.transform = 'status_map';
          next.source = 'field';
          next.fixedValue = undefined;
          next.valueMap = next.valueMap ?? {};
        }

        if (field.activitiesFieldSlug !== undefined && next.source === 'field') {
          const fd = selectedTypeFields.find((f) => f.slug === next.activitiesFieldSlug);
          if (next.jiraField === 'status') {
            next.transform = 'status_map';
          } else if (fd?.fieldType === 'reference') next.transform = 'reference_key';
          else if (fd?.fieldType === 'boolean') {
            next.transform = 'bool_map';
            next.valueMap = next.valueMap ?? {};
          } else if (fd?.fieldType === 'markdown' || fd?.fieldType === 'textarea') {
            if (next.transform !== 'markdown_to_jira_wiki') next.transform = next.transform;
          } else if (next.transform === 'reference_key' || next.transform === 'markdown_to_jira_wiki' || next.transform === 'status_map' || next.transform === 'bool_map') {
            next.transform = 'none';
          }
          // Колонка справочника применима только к reference-полю.
          if (fd?.fieldType !== 'reference') next.referenceColumnSlug = undefined;
        }
        return next;
      }),
    );
  };

  // ─── Save (real save, not simulation) ──────────────────────────────

  const handleSave = async () => {
    let values: ConfigFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    if (hasErrors) {
      void message.error('Исправьте ошибки маппинга перед сохранением');
      return;
    }

    // Filter out completely empty rows
    const cleanMappings = fieldMappings.filter(
      (m) => m.jiraField && (m.source === 'fixed' ? m.fixedValue?.trim() : m.activitiesFieldSlug),
    );

    setSaving(true);
    try {
      const cleanPullMappings = pullMappings.filter(
        (r) => r.jiraField && r.activitiesFieldSlug,
      );
      const pullFieldMapping = cleanPullMappings.length > 0
        ? { summaryTarget: values.summarySource || 'title', fields: cleanPullMappings }
        : null;

      // projectKeySource / issueTypeSource: пишем только когда field, иначе явный 'static' для очистки
      const baseSettings = (editingConfig?.settings ?? {}) as Record<string, unknown>;
      const nextSettings: Record<string, unknown> = { ...baseSettings };
      if (projectKeySourceKind === 'field' && projectKeyFieldId) {
        const sourceField = selectedTypeFields.find((f) => f.id === projectKeyFieldId);
        const isSelect = sourceField?.fieldType === 'select';
        const cleanMap = isSelect
          ? Object.fromEntries(
              Object.entries(projectKeyValueMap).filter(([, v]) => v && v.trim()),
            )
          : {};
        nextSettings.projectKeySource = {
          kind: 'field',
          fieldDefinitionId: projectKeyFieldId,
          ...(isSelect && Object.keys(cleanMap).length > 0 ? { valueMap: cleanMap } : {}),
        };
      } else {
        nextSettings.projectKeySource = { kind: 'static' };
      }
      if (issueTypeSourceKind === 'field' && issueTypeFieldId) {
        const cleanMap = Object.fromEntries(
          Object.entries(issueTypeValueMap).filter(([, v]) => v && v.trim()),
        );
        nextSettings.issueTypeSource = {
          kind: 'field',
          fieldDefinitionId: issueTypeFieldId,
          ...(Object.keys(cleanMap).length > 0 ? { valueMap: cleanMap } : {}),
        };
      } else {
        nextSettings.issueTypeSource = { kind: 'static' };
      }

      // Когда issueType резолвится из поля — статическое значение всё равно
      // обязано быть непустой строкой (шарёная схема `min(1)`); кладём
      // первое значение из valueMap или дефолтный «Task», чтобы пройти валидацию.
      const fallbackIssueType =
        issueTypeSourceKind === 'field'
          ? Object.values(issueTypeValueMap).find((v) => v && v.trim()) || 'Task'
          : values.jiraIssueType;
      // Аналогично для projectKey: при field-source оставляем то, что в форме,
      // либо первое значение valueMap; пустую строку схема не примет.
      const fallbackProjectKey =
        projectKeySourceKind === 'field'
          ? values.jiraProjectKey?.trim()
            || Object.values(projectKeyValueMap).find((v) => v && v.trim())
            || 'PROJ'
          : values.jiraProjectKey;
      const payload = {
        jiraProjectKey: fallbackProjectKey,
        jiraIssueType: fallbackIssueType,
        syncDirection: values.syncDirection,
        fieldMapping: {
          summarySource: values.summarySource,
          fields: cleanMappings,
        },
        pullFieldMapping,
        settings: nextSettings,
      };

      if (editingConfig) {
        const updated = await updateSyncConfig(spaceId, values.itemTypeId, payload);
        setConfigs((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      } else {
        const created = await createSyncConfig(spaceId, { itemTypeId: values.itemTypeId, ...payload });
        setConfigs((prev) => [...prev, created]);
      }
      setModalOpen(false);
      void message.success('Маппинг сохранён');
    } catch (err: any) {
      // Серверная валидация: разворачиваем список issues, чтобы админ видел
      // что именно отклонил бэк (см. apps/api/src/modules/jira/validate-config.service.ts).
      if (err?.code === 'validation_failed' && Array.isArray(err?.details?.issues)) {
        const issues = err.details.issues as Array<{ severity: string; message: string }>;
        const errorList = issues
          .filter((i) => i.severity === 'error')
          .map((i) => `• ${i.message}`)
          .join('\n');
        Modal.error({
          title: 'Маппинг не прошёл валидацию',
          content: <div style={{ whiteSpace: 'pre-line' }}>{errorList}</div>,
          width: 560,
        });
      } else {
        void message.error(err.message ?? 'Не удалось сохранить');
      }
    } finally {
      setSaving(false);
    }
  };

  // ─── Preview (real data dry-run) ──────────────────────────────────

  // Тихий запуск: не показывает тосты, используется для auto-refresh, пока drawer открыт.
  const runPreview = useCallback(
    async (opts: { silent: boolean }): Promise<void> => {
      const values = form.getFieldsValue();
      if (!values.itemTypeId || !values.summarySource) {
        if (!opts.silent) {
          void message.warning(
            !values.itemTypeId ? 'Сначала выберите тип записи' : 'Выберите поле для Summary',
          );
        }
        return;
      }
      setPreviewLoading(true);
      try {
        const cleanMappings = fieldMappings.filter(
          (m) => m.jiraField && (m.source === 'fixed' ? m.fixedValue?.trim() : m.activitiesFieldSlug),
        );
        const draft = {
          fieldMapping: {
            summarySource: values.summarySource,
            fields: cleanMappings,
          },
        };
        const data = await pushPreview(spaceId, values.itemTypeId, undefined, draft);
        setPreviewData(data);
      } catch (err: any) {
        if (!opts.silent) {
          void message.error(err.message ?? 'Не удалось получить предпросмотр');
        }
        setPreviewData(null);
      } finally {
        setPreviewLoading(false);
      }
    },
    [form, fieldMappings, spaceId],
  );

  const handlePushPreview = useCallback(async (): Promise<void> => {
    setPreviewOpen(true);
    await runPreview({ silent: false });
  }, [runPreview]);

  // Auto-refresh preview по изменениям маппинга, пока drawer открыт. Debounce 600ms,
  // чтобы не ходить на бэк на каждое нажатие клавиши.
  useEffect(() => {
    if (!previewOpen) return;
    const t = setTimeout(() => { void runPreview({ silent: true }); }, 600);
    return () => clearTimeout(t);
  }, [previewOpen, fieldMappings, runPreview]);

  // ─── AI suggest mapping ────────────────────────────────────────────

  const handlePushAiSuggest = async () => {
    if (selectedTypeFields.length === 0 || jiraFields.length === 0) {
      void message.warning('Сначала выберите тип записи, проект и тип задачи Jira');
      return;
    }
    setAiSuggesting(true);
    try {
      const suggestions = await suggestMapping(
        selectedTypeFields.map((f) => ({ slug: f.slug, name: f.name, type: f.fieldType })),
        jiraFields.map((f) => ({ key: f.key, name: f.name, required: f.required })),
      );
      const newMappings = buildPushMappingsFromSuggestions(suggestions);
      setFieldMappings(newMappings);
      void message.success(`Lensy предложил ${newMappings.length} маппингов`);
    } catch (err: any) {
      void message.error(err.message ?? 'Не удалось получить подсказки от Lensy');
    } finally {
      setAiSuggesting(false);
    }
  };

  const handlePullAiSuggest = async () => {
    if (selectedTypeFields.length === 0 || jiraFields.length === 0) {
      void message.warning('Сначала выберите тип записи, проект и тип задачи Jira');
      return;
    }
    setAiSuggesting(true);
    try {
      const suggestions = await suggestMapping(
        selectedTypeFields.map((f) => ({ slug: f.slug, name: f.name, type: f.fieldType })),
        jiraFields.map((f) => ({ key: f.key, name: f.name, required: f.required })),
      );
      const newMappings = buildPushMappingsFromSuggestions(suggestions)
        .filter((row) => row.activitiesFieldSlug && row.jiraField)
      .map((row) => ({
        jiraField: row.jiraField,
        activitiesFieldSlug: row.activitiesFieldSlug,
        transform: convertPushTransformToPull(row.transform),
        valueMap: row.valueMap,
      }));
      setPullMappings(newMappings);
      const summarySource = (form.getFieldValue('summarySource') as string | undefined) || 'title';
      setPullSummaryTarget(summarySource);
      void message.success(`Lensy предложил ${newMappings.length} pull-маппингов`);
    } catch (err: any) {
      void message.error(err.message ?? 'Не удалось получить подсказки от Lensy');
    } finally {
      setAiSuggesting(false);
    }
  };

  // ─── Push execute (bulk) из карточки ────────────────────────────────

  const handlePushExecute = async (config: JiraSyncConfig) => {
    const typeName = itemTypes.find((t) => t.id === config.itemTypeId)?.name ?? config.itemTypeId;
    Modal.confirm({
      title: 'Запустить выгрузку в Jira?',
      content: `Будут выгружены все записи типа «${typeName}» в Jira-проект ${config.jiraProjectKey}.`,
      okText: 'Выгрузить',
      cancelText: 'Отмена',
      onOk: async () => {
        setPushExecuteLoading(config.itemTypeId);
        try {
          // Собираем все itemIds данного типа через POST /items/query с курсорной
          // пагинацией. GET-роута списка нет; cap limit на бэке — 500, поэтому
          // тянем страницами по 500, пока nextCursor !== null.
          const itemIds: string[] = [];
          let cursor: string | undefined;
          do {
            const page = await queryItems(spaceId, {
              itemTypeIds: [config.itemTypeId],
              limit: 500,
              ...(cursor ? { cursor } : {}),
            });
            for (const it of page.items) itemIds.push(it.id);
            cursor = page.nextCursor ?? undefined;
          } while (cursor);
          if (itemIds.length === 0) {
            void message.info('Нет записей для выгрузки');
            return;
          }
          const { jobId } = await bulkJiraPush(spaceId, { itemIds });
          registerJob(jobId, `Синхронизация в Jira · ${itemIds.length} записей`);
          void message.info(`Задача запущена (job ${jobId}). Следим за прогрессом…`);

          // Poll job status
          let done = false;
          while (!done) {
            await new Promise((r) => setTimeout(r, 1500));
            const job = await getJob(jobId);
            if (job.status === 'success') {
              done = true;
              void message.success('Выгрузка завершена');
            } else if (job.status === 'error' || job.status === 'cancelled') {
              done = true;
              void message.error('Выгрузка завершилась с ошибкой');
            }
          }
        } catch (err: any) {
          void message.error(err.message ?? 'Не удалось запустить выгрузку');
        } finally {
          setPushExecuteLoading(null);
        }
      },
    });
  };

  // ─── Jira link bulk actions ─────────────────────────────────────────

  const handlePushAllJiraLinks = async (config: JiraSyncConfig | null) => {
    if (!config) return;
    setPushLinksRunning(true);
    try {
      const result = await syncLinksPush(spaceId, config.itemTypeId);
      void message.success(
        `Связи Jira: создано ${result.linksCreated}, пропущено ${result.skipped}` +
          (result.errors.length > 0 ? `, ошибок ${result.errors.length}` : ''),
      );
    } catch (err: any) {
      void message.error(err.message ?? 'Не удалось обновить связи в Jira');
    } finally {
      setPushLinksRunning(false);
    }
  };

  // ─── Run-sync modal: открытие и запуск загрузки из сохранённого конфига ──

  const openRunModal = (config: JiraSyncConfig) => {
    setRunConfig(config);
    setRunJql(`project = ${config.jiraProjectKey} AND assignee = currentUser()`);
    setRunMode('create_only');
    setRunMaxResults(100);
    setRunSummaryTarget(config.fieldMapping?.summarySource || 'title');
    setRunFields([]);
    void getItemType(spaceId, config.itemTypeId)
      .then((it) => setRunFields(it.fields))
      .catch(() => setRunFields([]));
  };

  const buildPullRequestFromConfig = (config: JiraSyncConfig) => {
    const pm = (config as any).pullFieldMapping as
      | { summaryTarget?: string; fields?: PullMappingRow[] }
      | null
      | undefined;
    const cleaned = (pm?.fields ?? []).filter((r) => r.jiraField && r.activitiesFieldSlug);
    return {
      jql: runJql.trim(),
      itemTypeId: config.itemTypeId,
      fieldMapping: {
        summaryTarget: runSummaryTarget || 'title',
        fields: cleaned,
      },
      mode: runMode,
      maxResults: runMaxResults,
    };
  };

  const handleRunPullPreview = async () => {
    if (!runConfig) return;
    if (!runJql.trim()) { void message.warning('Укажите JQL'); return; }
    setPullPreviewLoading(true);
    setPullPreviewOpen(true);
    setPullPreviewData(null);
    setPullExecuteResult(null);
    try {
      const data = await pullPreview(spaceId, buildPullRequestFromConfig(runConfig));
      setPullPreviewData(data);
    } catch (err: any) {
      void message.error(err.message ?? 'Не удалось получить предпросмотр загрузки');
      setPullPreviewOpen(false);
    } finally {
      setPullPreviewLoading(false);
    }
  };

  const handleRunPullExecute = () => {
    if (!runConfig) return;
    if (!runJql.trim()) { void message.warning('Укажите JQL'); return; }
    const config = runConfig;
    Modal.confirm({
      title: 'Запустить загрузку из Jira?',
      content: `JQL: ${runJql}. Режим: ${runMode === 'create_only' ? 'только создание' : 'создание и обновление'}.`,
      okText: 'Загрузить',
      cancelText: 'Отмена',
      onOk: async () => {
        setPullExecuteLoading(true);
        try {
          const result = await pullExecute(spaceId, buildPullRequestFromConfig(config));
          setPullExecuteResult(result);
          setPullPreviewOpen(true);
          void message.success(
            `Загружено: создано ${result.created}, обновлено ${result.updated}, пропущено ${result.skipped}`,
          );
        } catch (err: any) {
          void message.error(err.message ?? 'Ошибка при загрузке из Jira');
        } finally {
          setPullExecuteLoading(false);
        }
      },
    });
  };

  // ─── Reuse mapping between directions (push ↔ pull) ─────────────────

  const handleFillPullFromPush = () => {
    const values = form.getFieldsValue();
    const converted: PullMappingRow[] = fieldMappings
      .filter((r) => r.source !== 'fixed' && r.jiraField && r.activitiesFieldSlug)
      .map((r) => ({
        jiraField: r.jiraField,
        activitiesFieldSlug: r.activitiesFieldSlug,
        transform: convertPushTransformToPull((r.transform ?? 'none') as PushTransform),
        valueMap: r.valueMap,
      }));
    if (converted.length === 0) {
      void message.warning('В push-маппинге нет строк, пригодных для pull (fixed-строки пропускаются)');
      return;
    }
    setPullMappings(converted);
    if (values.summarySource) setPullSummaryTarget(values.summarySource);
    const skipped = fieldMappings.filter((r) => r.source === 'fixed').length;
    void message.success(
      `Pull-маппинг заполнен из push (${converted.length} строк)` +
        (skipped > 0 ? `, пропущено fixed: ${skipped}` : ''),
    );
  };

  const handleFillPushFromPull = () => {
    const converted: FieldMappingRow[] = pullMappings
      .filter((r) => r.jiraField && r.activitiesFieldSlug)
      .map((r) => ({
        activitiesFieldSlug: r.activitiesFieldSlug,
        jiraField: r.jiraField,
        transform: convertPullTransformToPush((r.transform ?? 'none') as PullTransform),
        source: 'field' as const,
        valueMap: r.valueMap,
      }));
    if (converted.length === 0) {
      void message.warning('В pull-маппинге нет строк для переноса');
      return;
    }
    setFieldMappings(converted);
    if (pullSummaryTarget) form.setFieldsValue({ summarySource: pullSummaryTarget });
    void message.success(`Push-маппинг заполнен из pull (${converted.length} строк)`);
  };

  // ─── Copy mapping from another itemType ─────────────────────────────

  const handleCopyMapping = async () => {
    if (!editingConfig || !copyModalOpen || !copySourceItemTypeId) return;
    try {
      const target = copyModalOpen.direction;
      // Источник = текущий itemType + кросс-направление: применяем конверсию локально
      // через те же хелперы, что и кнопки «Заполнить из Push/Pull» — без round-trip в API.
      if (
        copySourceItemTypeId === editingConfig.itemTypeId &&
        target !== 'both' &&
        target !== copySourceDirection
      ) {
        if (target === 'pull') handleFillPullFromPush();
        else handleFillPushFromPull();
        setCopyModalOpen(null);
        setCopySourceItemTypeId(null);
        return;
      }
      let direction: 'push' | 'pull' | 'both' | 'pushFromPull' | 'pullFromPush';
      if (target === 'both') {
        direction = 'both';
      } else if (target === copySourceDirection) {
        direction = target;
      } else {
        direction = target === 'push' ? 'pushFromPull' : 'pullFromPush';
      }
      const updated = await copyMappingBetween(spaceId, editingConfig.itemTypeId, {
        sourceItemTypeId: copySourceItemTypeId,
        direction,
      });
      setConfigs((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      // Reflect in edit modal
      setFieldMappings(
        updated.fieldMapping.fields.map((f) => ({
          activitiesFieldSlug: f.activitiesFieldSlug,
          jiraField: f.jiraField,
          transform: (f.transform ?? 'none') as FieldMappingRow['transform'],
          source: (f.source ?? 'field') as 'field' | 'fixed',
          fixedValue: f.fixedValue,
          valueMap: f.valueMap,
        })),
      );
      const pm = (updated as any).pullFieldMapping;
      if (pm) {
        setPullMappings(pm.fields ?? []);
      }
      setCopyModalOpen(null);
      setCopySourceItemTypeId(null);
      const skipped = (updated as any).copyReport?.skipped as
        | Array<{ direction: string; slug: string; jiraField: string; reason: string }>
        | undefined;
      if (skipped && skipped.length > 0) {
        const names = Array.from(new Set(skipped.map((s) => s.slug))).join(', ');
        void message.warning(
          `Маппинг скопирован частично: перенесено совместимое, пропущено полей — ${skipped.length} (${names})`,
        );
      } else {
        void message.success('Маппинг скопирован');
      }
    } catch (err: any) {
      void message.error(err.message ?? 'Не удалось скопировать маппинг');
    }
  };

  // ─── Pull mapping table helpers ─────────────────────────────────────

  const addPullMappingRow = () => {
    setPullMappings((prev) => [...prev, { jiraField: '', activitiesFieldSlug: '', transform: 'none', valueMap: {} }]);
  };

  const updatePullMapping = (index: number, patch: Partial<PullMappingRow>) => {
    setPullMappings((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, ...patch };
        const jiraMeta = jiraFields.find((jiraField) => jiraField.key === next.jiraField);
        if (patch.jiraField === 'status' || jiraMeta?.schema.type === 'status') {
          next.transform = 'status_map_reverse';
          next.valueMap = next.valueMap ?? {};
        }
        return next;
      }),
    );
  };

  const removePullMapping = (index: number) => {
    setPullMappings((prev) => prev.filter((_, i) => i !== index));
  };

  const getLensFieldOptionsForJiraField = (jiraFieldKey: string) => {
    const fields = jiraFieldKey === 'status'
      ? selectedTypeFields.filter((field) => isLensStatusField(field))
      : selectedTypeFields;

    return fields.map((field) => ({
      value: field.slug,
      label: field.name,
    }));
  };

  const loadTransitionGraph = async () => {
    if (!editingConfig) return;
    setTransitionGraphLoading(true);
    try {
      const graph = await fetchJiraTransitionGraph(spaceId, editingConfig.itemTypeId);
      setTransitionGraph(graph);
    } catch {
      setTransitionGraph(null);
    } finally {
      setTransitionGraphLoading(false);
    }
  };

  const handleScanTransitionGraph = async () => {
    if (!editingConfig) return;
    const keys = transitionScanKeys
      .split(/[\s,;]+/)
      .map((k) => k.trim())
      .filter(Boolean);
    if (keys.length === 0) {
      message.warning('Укажите хотя бы один ключ задачи (например EKF12-37)');
      return;
    }
    setTransitionScanLoading(true);
    try {
      const res = await scanJiraTransitionGraph(spaceId, editingConfig.itemTypeId, keys.slice(0, 20));
      message.success(`Просканировано задач: ${res.scanned}. Рёбер в графе: ${res.edges.length}`);
      await loadTransitionGraph();
    } catch (err: any) {
      void message.error(err?.message ?? 'Не удалось просканировать workflow');
    } finally {
      setTransitionScanLoading(false);
    }
  };

  const handleScanAllTransitionGraph = async () => {
    if (!editingConfig) return;
    setTransitionScanAllLoading(true);
    try {
      const res = await scanAllJiraTransitionGraph(spaceId, editingConfig.itemTypeId);
      const missing = res.missingStatuses ?? [];
      void message.success(
        `Просканировано статусов: ${res.scanned}. Рёбер в графе: ${res.edges.length}.` +
          (missing.length ? ` Без задач (рёбра неизвестны): ${missing.join(', ')}` : ''),
      );
      await loadTransitionGraph();
    } catch (err: any) {
      void message.error(err?.message ?? 'Не удалось просканировать workflow');
    } finally {
      setTransitionScanAllLoading(false);
    }
  };

  const openStatusMappingModal = (mode: 'push' | 'pull', index: number) => {
    const row = mode === 'push' ? fieldMappings[index] : pullMappings[index];
    if (!row) return;
    setStatusMappingEditor({
      mode,
      index,
      valueMap: { ...(row.valueMap ?? {}) },
    });
    // Диаграмма доступна только для уже сохранённого конфига (нужен syncConfigId).
    setTransitionGraph(null);
    setTransitionScanKeys('');
    if (mode === 'push' && editingConfig) {
      void loadTransitionGraph();
    }
  };

  const saveStatusMappingModal = () => {
    if (!statusMappingEditor) return;
    if (statusMappingEditor.mode === 'push') {
      updateFieldMapping(statusMappingEditor.index, { valueMap: statusMappingEditor.valueMap });
    } else {
      updatePullMapping(statusMappingEditor.index, { valueMap: statusMappingEditor.valueMap });
    }
    setStatusMappingEditor(null);
  };

  const openBoolMappingModal = (mode: 'push' | 'pull', index: number) => {
    const row = mode === 'push' ? fieldMappings[index] : pullMappings[index];
    if (!row) return;
    setBoolMappingEditor({ mode, index, valueMap: { ...(row.valueMap ?? {}) } });
  };

  const saveBoolMappingModal = () => {
    if (!boolMappingEditor) return;
    if (boolMappingEditor.mode === 'push') {
      updateFieldMapping(boolMappingEditor.index, { valueMap: boolMappingEditor.valueMap });
    } else {
      updatePullMapping(boolMappingEditor.index, { valueMap: boolMappingEditor.valueMap });
    }
    setBoolMappingEditor(null);
  };

  const suggestStatusMappingWithLensy = () => {
    if (!statusMappingEditor) return;
    const row = statusMappingEditor.mode === 'push'
      ? fieldMappings[statusMappingEditor.index]
      : pullMappings[statusMappingEditor.index];
    const lensField = row
      ? selectedTypeFields.find((f) => f.slug === row.activitiesFieldSlug)
      : undefined;
    const options = ((lensField?.config as { options?: Array<{ value: string; label: string; category?: string }> } | undefined)?.options ?? []);
    if (options.length === 0 || jiraStatuses.length === 0) return;

    const norm = (s: string) => s.toLowerCase().trim().replace(/[\s_\-]+/g, ' ');
    const categoryHints: Record<string, string[]> = {
      backlog: ['backlog', 'open', 'to do', 'todo', 'новая', 'открыт', 'к работе', 'queued'],
      active: ['in progress', 'progress', 'doing', 'review', 'в работе', 'активн', 'разработ'],
      done: ['done', 'closed', 'resolved', 'complete', 'готово', 'закрыт', 'завершен'],
      cancelled: ['cancelled', 'canceled', 'rejected', 'won\'t do', 'отмен', 'отклон'],
    };

    const next: Record<string, string> = { ...statusMappingEditor.valueMap };
    const used = new Set<string>(Object.values(next));

    for (const opt of options) {
      if (next[opt.value]) continue;
      const optName = norm(opt.label);
      let pick = jiraStatuses.find((s) => !used.has(s.name) && norm(s.name) === optName);
      if (!pick) pick = jiraStatuses.find((s) => !used.has(s.name) && (norm(s.name).includes(optName) || optName.includes(norm(s.name))));
      if (!pick && opt.category) {
        const hints = categoryHints[opt.category] ?? [];
        pick = jiraStatuses.find((s) => !used.has(s.name) && hints.some((h) => norm(s.name).includes(h)));
      }
      if (pick) {
        next[opt.value] = pick.name;
        used.add(pick.name);
      }
    }

    setStatusMappingEditor((cur) => (cur ? { ...cur, valueMap: next } : cur));
  };

  // ─── Render helpers ──────────────────────────────────────────────────

  const renderLensFieldCell = (row: FieldMappingRow & { _index: number }, index: number) => {
    const isFixed = row.source === 'fixed';
    const isCurrentUser = isFixed && row.fixedValue === CURRENT_USER_LOGIN_TOKEN;
    const jiraMeta = jiraFields.find((f) => f.key === row.jiraField);
    const hasAllowedValues = jiraMeta?.allowedValues && jiraMeta.allowedValues.length > 0;
    const userShaped = isUserShapedJiraField(row.jiraField, jiraMeta);

    // Внутренний режим Segmented'а: 'field' / 'fixed' / 'me'.
    // 'me' хранится как source='fixed' + fixedValue='@me', чтобы не менять схему.
    const mode: 'field' | 'fixed' | 'me' = isCurrentUser ? 'me' : row.source;

    const segmentedOptions = row.jiraField === 'status'
      ? [{ label: 'Поле', value: 'field' as const }]
      : [
          { label: 'Поле', value: 'field' as const },
          { label: 'Значение', value: 'fixed' as const },
          ...(userShaped ? [{ label: 'Я', value: 'me' as const }] : []),
        ];

    const onModeChange = (next: 'field' | 'fixed' | 'me') => {
      if (next === 'me') {
        updateFieldMapping(index, {
          source: 'fixed',
          fixedValue: CURRENT_USER_LOGIN_TOKEN,
          activitiesFieldSlug: '',
        });
      } else if (next === 'fixed') {
        updateFieldMapping(index, { source: 'fixed', fixedValue: '', activitiesFieldSlug: '' });
      } else {
        updateFieldMapping(index, { source: 'field', fixedValue: undefined, activitiesFieldSlug: '' });
      }
    };

    return (
      <div className="jira-source-cell" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Segmented
          size="small"
          value={mode}
          onChange={(v) => onModeChange(v as 'field' | 'fixed' | 'me')}
          options={segmentedOptions}
        />
        {isCurrentUser ? (
          <Tooltip title="Логин подставится в момент пуша — это будет логин пользователя, нажавшего кнопку «Push в Jira».">
            <Tag color="blue" style={{ marginInlineEnd: 0 }}>Логин текущего пользователя</Tag>
          </Tooltip>
        ) : isFixed ? (
          hasAllowedValues ? (
            <Select
              size="small"
              style={{ width: '100%' }}
              placeholder="Выберите значение"
              value={row.fixedValue || undefined}
              onChange={(v) => updateFieldMapping(index, { fixedValue: v })}
              showSearch
              optionFilterProp="label"
              options={jiraMeta!.allowedValues!.map((av) => ({
                value: av.id,
                label: av.name ?? av.value ?? av.id,
              }))}
            />
          ) : (
            <Input
              size="small"
              placeholder="Фиксированное значение"
              value={row.fixedValue ?? ''}
              onChange={(e) => updateFieldMapping(index, { fixedValue: e.target.value })}
            />
          )
        ) : selectedTypeFields.length > 0 ? (
          <Select
            size="small"
            style={{ width: '100%' }}
            popupMatchSelectWidth={false}
            placeholder="Выберите"
            value={row.activitiesFieldSlug || undefined}
            onChange={(v) => updateFieldMapping(index, { activitiesFieldSlug: v })}
            showSearch
            optionFilterProp="label"
            options={getLensFieldOptionsForJiraField(row.jiraField)
              .filter((field) => {
                // reference-поля можно переиспользовать: одно поле-ссылку
                // легитимно мапить в несколько Jira-полей через разные колонки
                // справочника (referenceColumnSlug) — например исполнитель → assignee
                // и тот же исполнитель → его тех.лид.
                const isReference =
                  selectedTypeFields.find((f) => f.slug === field.value)?.fieldType === 'reference';
                if (isReference || field.value === row.activitiesFieldSlug) return true;
                return !fieldMappings.some(
                  (mapping, mappingIndex) =>
                    mappingIndex !== index &&
                    mapping.source === 'field' &&
                    mapping.activitiesFieldSlug === field.value,
                );
              })
              .map((field) => ({
                value: field.value,
                label: field.label,
              }))}
          />
        ) : (
          <Input
            size="small"
            placeholder="slug"
            value={row.activitiesFieldSlug}
            onChange={(e) => updateFieldMapping(index, { activitiesFieldSlug: e.target.value })}
          />
        )}
      </div>
    );
  };

  const renderJiraFieldSelect = (val: string, index: number) =>
    jiraFields.length > 0 ? (
      <Select
        size="small"
        style={{ width: '100%' }}
        popupMatchSelectWidth={false}
        placeholder="Выберите"
        value={val || undefined}
        onChange={(v) => updateFieldMapping(index, { jiraField: v })}
        showSearch
        optionFilterProp="label"
        options={jiraFields
          .filter(
            (f) =>
              f.key === val ||
              (!EXCLUDED_JIRA_FIELDS.has(f.key) &&
                !fieldMappings.some((m, j) => j !== index && m.jiraField === f.key)),
          )
          .map((f) => ({
            value: f.key,
            label: `${f.name}${f.required ? ' *' : ''}`,
          }))}
      />
    ) : (
      <Input
        size="small"
        placeholder="assignee, customfield_..."
        value={val}
        onChange={(e) => updateFieldMapping(index, { jiraField: e.target.value })}
      />
    );

  const renderValidationCell = (validations: Record<number, MappingValidation>, index: number) => {
    const validation = validations[index];
    if (!validation) return <Typography.Text type="secondary">—</Typography.Text>;
    if (validation.status === 'ok') {
      return <StatusIcon status={validation.status} />;
    }
    const color = validation.status === 'error' ? 'var(--color-error)' : 'var(--color-warning)';
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, maxWidth: 320 }}>
        <span style={{ flex: '0 0 auto', lineHeight: '20px' }}>
          <StatusIcon status={validation.status} />
        </span>
        <span style={{ fontSize: 12, color, whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {validation.message}
        </span>
      </div>
    );
  };

  // ─── Preview modal content ──────────────────────────────────────────

  const renderPreviewField = (field: PushPreviewField) => {
    const statusColor = field.status === 'ok' ? 'success' : field.status === 'warning' ? 'warning' : 'error';

    return (
      <div
        key={`${field.jiraField}-${field.lensFieldSlug}`}
        style={{
          padding: '8px 12px',
          marginBottom: 4,
          borderRadius: radius.sm,
          background: field.status === 'error' ? 'var(--color-error-bg)' : field.status === 'warning' ? 'var(--color-warning-bg)' : 'var(--color-success-bg)',
          border: `1px solid ${field.status === 'error' ? 'var(--color-error)' : field.status === 'warning' ? 'var(--color-warning)' : 'var(--color-success)'}`,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <Typography.Text strong style={{ fontSize: 12 }}>
              {field.lensFieldName ?? '(фикс.)'}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
              → {field.jiraField}
            </Typography.Text>
          </div>
          <Tag color={statusColor} style={{ marginRight: 0 }}>
            {field.status.toUpperCase()}
          </Tag>
        </div>

        <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: 12 }}>
          <div>
            <Typography.Text type="secondary">Lens: </Typography.Text>
            <Typography.Text code style={{ fontSize: 11 }}>
              {field.lensValue != null ? formatPreviewValue(field.lensValue) : '(пусто)'}
            </Typography.Text>
          </div>
          <div>
            <Typography.Text type="secondary">Jira: </Typography.Text>
            <Typography.Text code style={{ fontSize: 11 }}>
              {field.jiraValue != null ? formatPreviewValue(field.jiraValue) : '(пусто)'}
            </Typography.Text>
          </div>
        </div>

        {field.transformSteps && field.transformSteps.length > 0 && (
          <div style={{ marginTop: 4 }}>
            {field.transformSteps.map((step, i) => (
              <div key={i} style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>↳ {step}</div>
            ))}
          </div>
        )}

        {field.message && (
          <Typography.Text type={field.status === 'error' ? 'danger' : 'warning'} style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
            {field.message}
          </Typography.Text>
        )}
      </div>
    );
  };

  const statusMappingRow = statusMappingEditor
    ? (statusMappingEditor.mode === 'push'
      ? fieldMappings[statusMappingEditor.index]
      : pullMappings[statusMappingEditor.index])
    : null;
  const statusMappingLensField = statusMappingRow
    ? selectedTypeFields.find((field) => field.slug === statusMappingRow.activitiesFieldSlug)
    : undefined;
  const statusMappingOptions = ((statusMappingLensField?.config as {
    options?: Array<{ value: string; label: string; category?: string }>;
  } | undefined)?.options ?? []);

  // ─── Bool mapping modal helpers ─────────────────────────────────────
  const boolMappingRow = boolMappingEditor
    ? (boolMappingEditor.mode === 'push'
      ? fieldMappings[boolMappingEditor.index]
      : pullMappings[boolMappingEditor.index])
    : null;
  const boolJiraMeta = boolMappingRow
    ? jiraFields.find((f) => f.key === boolMappingRow.jiraField)
    : undefined;
  const boolAllowedValues = (boolJiraMeta?.allowedValues ?? []).map(
    (av) => av.value ?? av.name ?? av.id,
  ).filter((v): v is string => !!v);

  // ─── Main table columns ─────────────────────────────────────────────

  const mainColumns = [
    {
      title: 'Тип записи',
      dataIndex: 'itemTypeId',
      key: 'itemTypeId',
      render: (id: string) => {
        const t = itemTypes.find((it) => it.id === id);
        return t ? t.name : id;
      },
    },
    {
      title: 'Jira-проект',
      dataIndex: 'jiraProjectKey',
      key: 'jiraProjectKey',
      render: (key: string) => <Tag color="blue">{key}</Tag>,
    },
    {
      title: 'Тип задачи',
      dataIndex: 'jiraIssueType',
      key: 'jiraIssueType',
    },
    ...(isAdmin
      ? [
          {
            title: '',
            key: 'actions',
            width: 140,
            render: (_: unknown, record: JiraSyncConfig) => (
              <Space>
                <Tooltip title="Запустить синхронизацию">
                  <Button
                    size="small"
                    type="primary"
                    ghost
                    icon={<SyncOutlined />}
                    onClick={() => openRunModal(record)}
                  />
                </Tooltip>
                <Tooltip title="Настроить маппинг">
                  <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                </Tooltip>
                <Tooltip title="Удалить маппинг">
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
                </Tooltip>
              </Space>
            ),
          },
        ]
      : []),
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 64 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {error && <Alert type="error" message={error} showIcon closable onClose={() => setError(null)} />}

      <Card
        title="Jira-маппинги"
        style={{ borderRadius: radius.lg, boxShadow: shadows.card }}
        extra={
          isAdmin ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Добавить маппинг
            </Button>
          ) : null
        }
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: spacing.base }}>
          Каждый маппинг связывает тип записи в пространстве с проектом и типом задачи в Jira.
        </Typography.Paragraph>

        {configs.length === 0 ? (
          <Empty description="Нет настроенных маппингов" style={{ padding: `${spacing.lg}px 0` }} />
        ) : (
          <Table<JiraSyncConfig>
            rowKey="id"
            columns={mainColumns}
            dataSource={configs}
            pagination={false}
            size="small"
          />
        )}
      </Card>

      {/* Авто-синхронизация по cron'у переехала во вкладку «Автоматизации»
          (FR-12 unification): создавайте правило с одним шагом jira.pull
          или jira.push. */}

      {/* ─── Edit / Create modal ──────────────────────────────────── */}
      <Modal
        title={editingConfig ? 'Редактировать маппинг' : 'Новый Jira-маппинг'}
        className="jira-mapping-modal"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)}>Отмена</Button>,
          <Tooltip
            key="save"
            title={
              hasErrors
                ? `Исправьте ошибки маппинга перед сохранением (вкладк${pushHasErrors && pullHasErrors ? 'и' : 'а'} ${[
                    pushHasErrors ? 'Выгрузка (Push)' : null,
                    pullHasErrors ? 'Загрузка (Pull)' : null,
                  ]
                    .filter(Boolean)
                    .join(' и ')})`
                : ''
            }
          >
            <Button
              type="primary"
              loading={saving}
              disabled={hasErrors}
              onClick={handleSave}
            >
              Сохранить
            </Button>
          </Tooltip>,
        ].filter(Boolean)}
        destroyOnClose
        width={1080}
      >
        <Form<ConfigFormValues> form={form} layout="vertical" requiredMark>
          {/* ─── 1. Контекст: к чему привязан маппинг ───────────────── */}
          <Typography.Text
            style={{
              display: 'block',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              color: 'var(--color-text-secondary)',
              marginBottom: 6,
            }}
          >
            Куда синхронизируем
          </Typography.Text>

          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 10 }}>
            <Form.Item
              label="Тип записи"
              name="itemTypeId"
              rules={[{ required: true, message: 'Выберите тип записи' }]}
              tooltip={editingConfig ? 'Тип записи нельзя изменить у существующего маппинга — создайте новый маппинг для другого типа' : undefined}
              style={{ marginBottom: 12 }}
            >
              <Select
                placeholder="Выберите тип записи"
                disabled={!!editingConfig}
                onChange={handleItemTypeChange}
                options={availableItemTypes.map((t) => ({
                  value: t.id,
                  label: t.name,
                }))}
              />
            </Form.Item>

            <Form.Item
              label="Jira-проект"
              name="jiraProjectKey"
              rules={[{ required: true, message: 'Укажите проект' }]}
              style={{ marginBottom: 12 }}
            >
              {jiraAvailable ? (
                <Select
                  placeholder={jiraProjectsLoading ? 'Загрузка проектов…' : 'Начните вводить ключ…'}
                  loading={jiraProjectsLoading}
                  onChange={handleProjectChange}
                  showSearch
                  virtual
                  notFoundContent={jiraProjectsLoading ? <Spin size="small" /> : 'Проекты не найдены'}
                  filterOption={(input, option) => {
                    const q = input.toLowerCase();
                    const key = (option?.value as string ?? '').toLowerCase();
                    const label = (option?.label as string ?? '').toLowerCase();
                    return key.includes(q) || label.includes(q);
                  }}
                  options={jiraProjects.map((p) => ({
                    value: p.key,
                    label: `${p.key} — ${p.name}`,
                  }))}
                />
              ) : (
                <Input placeholder="GOALS" />
              )}
            </Form.Item>

            <Form.Item
              label="Тип задачи"
              name="jiraIssueType"
              rules={[{ required: true, message: 'Укажите тип' }]}
              style={{ marginBottom: 12 }}
            >
              {jiraAvailable ? (
                <Select
                  placeholder={
                    jiraIssueTypesLoading
                      ? 'Загрузка типов…'
                      : jiraIssueTypes.length === 0
                        ? 'Сначала выберите проект'
                        : 'Выберите тип'
                  }
                  loading={jiraIssueTypesLoading}
                  onChange={handleIssueTypeChange}
                  notFoundContent={jiraIssueTypesLoading ? <Spin size="small" /> : 'Типы задач не найдены'}
                  options={jiraIssueTypes
                    .filter((t) => !t.subtask)
                    .map((t) => ({
                      value: t.name,
                      label: t.name,
                    }))}
                />
              ) : (
                <Input placeholder="Task" />
              )}
            </Form.Item>
          </div>

          <Form.Item name="syncDirection" hidden initialValue="both">
            <Input type="hidden" />
          </Form.Item>

          {/* ─── Свёрнутые секции: шаблон + расширенные источники ──── */}
          <Collapse
            ghost
            size="small"
            style={{ marginBottom: 8 }}
            items={[
              ...(templates.length > 0
                ? [{
                    key: 'template',
                    label: editingConfig ? 'Применить шаблон к текущему маппингу' : 'Применить шаблон',
                    children: (
                      <Form.Item
                        name="templateId"
                        extra={editingConfig ? 'Появится диалог выбора: дополнить или заменить текущие строки.' : undefined}
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          allowClear
                          placeholder="Выберите шаблон"
                          onChange={(v) => {
                            if (v) {
                              handleApplyTemplate(v);
                              form.setFieldsValue({ templateId: undefined });
                            }
                          }}
                          options={templates.map((t) => ({
                            value: t.id,
                            label: `${t.name}${t.isBuiltin ? ' (встроенный)' : ''}`,
                          }))}
                        />
                      </Form.Item>
                    ),
                  }]
                : []),
              {
                key: 'advanced',
                label: (
                  <span>
                    Дополнительно: переменный Jira-проект / тип задачи
                    {(projectKeySourceKind === 'field' || issueTypeSourceKind === 'field') && (
                      <Tag color="blue" style={{ marginLeft: 8 }}>включено</Tag>
                    )}
                  </span>
                ),
                children: (
                  <>
                    {/* Источник Jira-проекта */}
                    <Form.Item
                      label={
                        <Tooltip
                          title={
                            'Один проект — push всех записей этого типа уходит в выбранный выше Jira-проект. '
                            + 'Из поля записи — projectKey берётся из значения указанного поля каждой записи; '
                            + 'это позволяет в одном типе пушить разные записи в разные Jira-проекты.'
                          }
                        >
                          <span>Источник Jira-проекта</span>
                        </Tooltip>
                      }
                      style={{ marginBottom: 8 }}
                    >
                      <Radio.Group
                        value={projectKeySourceKind}
                        onChange={(e) => {
                          const next = e.target.value as 'static' | 'field';
                          setProjectKeySourceKind(next);
                          if (next === 'static') setProjectKeyFieldId(null);
                        }}
                      >
                        <Radio value="static">Один проект</Radio>
                        <Radio value="field">Из поля записи</Radio>
                      </Radio.Group>
                    </Form.Item>

                    {projectKeySourceKind === 'field' && (
                      <>
                        <Form.Item
                          label="Поле-источник projectKey"
                          required
                          validateStatus={!projectKeyFieldId ? 'error' : ''}
                          help={
                            !projectKeyFieldId
                              ? 'Выберите поле text, computed, reference или select. Для select настройте маппинг «опция → projectKey».'
                              : undefined
                          }
                        >
                          <Select
                            placeholder="Выберите поле"
                            value={projectKeyFieldId ?? undefined}
                            onChange={(v) => {
                              setProjectKeyFieldId(v as string);
                              setProjectKeyValueMap({});
                            }}
                            options={selectedTypeFields
                              .filter(
                                (f) =>
                                  f.fieldType === 'text' ||
                                  f.fieldType === 'computed' ||
                                  f.fieldType === 'reference' ||
                                  f.fieldType === 'select',
                              )
                              .map((f) => ({
                                value: f.id,
                                label: `${f.name} (${f.slug}) — ${f.fieldType}`,
                              }))}
                            notFoundContent="В этом типе нет полей text / computed / reference / select"
                          />
                        </Form.Item>
                        {(() => {
                          const fd = selectedTypeFields.find((f) => f.id === projectKeyFieldId);
                          if (!fd || fd.fieldType !== 'select') return null;
                          const cfg = (fd.config ?? {}) as { options?: Array<{ value: string; label?: string }> };
                          const options = Array.isArray(cfg.options) ? cfg.options : [];
                          if (options.length === 0) {
                            return (
                              <div style={{ color: 'var(--color-error)', fontSize: 12, marginBottom: 12 }}>
                                У выбранного select-поля нет опций.
                              </div>
                            );
                          }
                          return (
                            <Form.Item
                              label="Маппинг опций → Jira projectKey"
                              extra="Если для опции маппинг не задан — используется option.value как есть."
                            >
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                {options.map((opt) => (
                                  <React.Fragment key={opt.value}>
                                    <div style={{ alignSelf: 'center' }}>
                                      {opt.label ?? opt.value}{' '}
                                      <span style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>({opt.value})</span>
                                    </div>
                                    {jiraProjects.length > 0 ? (
                                      <Select
                                        showSearch
                                        allowClear
                                        placeholder="Выберите Jira-проект"
                                        value={projectKeyValueMap[opt.value] || undefined}
                                        onChange={(v) =>
                                          setProjectKeyValueMap((prev) => ({ ...prev, [opt.value]: (v as string) ?? '' }))
                                        }
                                        options={jiraProjects.map((p) => ({
                                          value: p.key,
                                          label: `${p.key} — ${p.name}`,
                                        }))}
                                      />
                                    ) : (
                                      <Input
                                        placeholder="GOALS"
                                        value={projectKeyValueMap[opt.value] ?? ''}
                                        onChange={(e) =>
                                          setProjectKeyValueMap((prev) => ({ ...prev, [opt.value]: e.target.value }))
                                        }
                                      />
                                    )}
                                  </React.Fragment>
                                ))}
                              </div>
                            </Form.Item>
                          );
                        })()}
                      </>
                    )}

                    {/* Источник Jira issue type */}
                    <Form.Item
                      label={
                        <Tooltip
                          title={
                            'Один тип — все записи push'
                            + "ятся в Jira с указанным выше типом задачи. "
                            + 'Из select-поля записи — тип берётся из значения select-поля через маппинг «опция → Jira issue type».'
                          }
                        >
                          <span>Источник Jira issue type</span>
                        </Tooltip>
                      }
                      style={{ marginBottom: 8 }}
                    >
                      <Radio.Group
                        value={issueTypeSourceKind}
                        onChange={(e) => {
                          const next = e.target.value as 'static' | 'field';
                          setIssueTypeSourceKind(next);
                          if (next === 'static') {
                            setIssueTypeFieldId(null);
                            setIssueTypeValueMap({});
                          }
                        }}
                      >
                        <Radio value="static">Один тип задачи</Radio>
                        <Radio value="field">Из select-поля записи</Radio>
                      </Radio.Group>
                    </Form.Item>

                    {issueTypeSourceKind === 'field' && (
                      <>
                        <Form.Item
                          label="Поле-источник issue type"
                          required
                          validateStatus={!issueTypeFieldId ? 'error' : ''}
                          help={
                            !issueTypeFieldId
                              ? 'Выберите select-поле. Каждой опции сопоставьте имя Jira issue type.'
                              : undefined
                          }
                        >
                          <Select
                            placeholder="Выберите select-поле"
                            value={issueTypeFieldId ?? undefined}
                            onChange={(v) => {
                              setIssueTypeFieldId(v as string);
                              setIssueTypeValueMap({});
                            }}
                            options={selectedTypeFields
                              .filter((f) => f.fieldType === 'select')
                              .map((f) => ({
                                value: f.id,
                                label: `${f.name} (${f.slug})`,
                              }))}
                            notFoundContent="В этом типе нет select-полей"
                          />
                        </Form.Item>
                        {(() => {
                          const fd = selectedTypeFields.find((f) => f.id === issueTypeFieldId);
                          if (!fd) return null;
                          const cfg = (fd.config ?? {}) as { options?: Array<{ value: string; label?: string }> };
                          const options = Array.isArray(cfg.options) ? cfg.options : [];
                          if (options.length === 0) {
                            return (
                              <div style={{ color: 'var(--color-error)', fontSize: 12, marginBottom: 12 }}>
                                У выбранного select-поля нет опций.
                              </div>
                            );
                          }
                          return (
                            <Form.Item
                              label="Маппинг опций → Jira issue type"
                              extra="Если для опции маппинг не задан — используется option.value как имя issue type."
                            >
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                {options.map((opt) => (
                                  <React.Fragment key={opt.value}>
                                    <div style={{ alignSelf: 'center' }}>
                                      {opt.label ?? opt.value}{' '}
                                      <span style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>({opt.value})</span>
                                    </div>
                                    {jiraAvailable && jiraIssueTypes.length > 0 ? (
                                      <Select
                                        showSearch
                                        allowClear
                                        placeholder="Выберите Jira issue type"
                                        value={issueTypeValueMap[opt.value] || undefined}
                                        onChange={(v) =>
                                          setIssueTypeValueMap((prev) => ({ ...prev, [opt.value]: (v as string) ?? '' }))
                                        }
                                        options={jiraIssueTypes
                                          .filter((t) => !t.subtask)
                                          .map((t) => ({ value: t.name, label: t.name }))}
                                      />
                                    ) : (
                                      <Input
                                        placeholder="Task"
                                        value={issueTypeValueMap[opt.value] ?? ''}
                                        onChange={(e) =>
                                          setIssueTypeValueMap((prev) => ({ ...prev, [opt.value]: e.target.value }))
                                        }
                                      />
                                    )}
                                  </React.Fragment>
                                ))}
                              </div>
                            </Form.Item>
                          );
                        })()}
                      </>
                    )}
                  </>
                ),
              },
            ]}
          />

          {/* summarySource живёт в state формы, рисуется как закреплённая
              первая «системная» строка таблицы маппинга — см. ниже. */}
          <Form.Item name="summarySource" hidden rules={[{ required: true, message: 'Укажите поле' }]}>
            <Input type="hidden" />
          </Form.Item>
        </Form>

        {/* ─── Field mapping tabs (Push / Pull) ──────────────────────── */}
        <Tabs
          activeKey={mappingTab}
          onChange={(k) => setMappingTab(k as 'push' | 'pull')}
          style={{ marginTop: 8 }}
          items={[
            {
              key: 'push',
              label: (
                <span>
                  Выгрузка (Push)
                  {pushHasErrors && (
                    <CloseCircleOutlined style={{ color: 'var(--color-error)', fontSize: 13, marginLeft: 6 }} />
                  )}
                </span>
              ),
              children: (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <Typography.Text strong style={{ fontSize: 13 }}>Маппинг Lens → Jira</Typography.Text>
                    <Space size="small">
                      <Button size="small" type="primary" ghost onClick={addFieldMapping} icon={<PlusOutlined />}>
                        Добавить поле
                      </Button>
                      <Dropdown
                        trigger={['click']}
                        menu={{
                          style: { width: 320, padding: 6, borderRadius: 12 },
                          items: [
                            autoFillItem('automatch', <ThunderboltOutlined />, 'Авто-сопоставить по именам', 'По совпадению названий полей', {
                              disabled: mappingPrereqMissing !== null,
                              onClick: handleAutoMatch,
                            }),
                            autoFillItem('lensy', <AiSparkIcon />, 'Заполнить через Lensy', 'ИИ подберёт соответствия полей', {
                              disabled: mappingPrereqMissing !== null,
                              onClick: handlePushAiSuggest,
                            }),
                            { type: 'divider' as const },
                            autoFillItem('fill-from-pull', <SyncOutlined />, 'Заполнить из Pull', 'Перенести строки со вкладки «Загрузка»', {
                              disabled: pullMappings.length === 0,
                              onClick: handleFillPushFromPull,
                            }),
                            autoFillItem('from-jira', <CloudDownloadOutlined />, 'Добавить поля из Jira…', 'Подтянуть поля из примера задачи', {
                              disabled: !editingConfig,
                              onClick: () => setAddFieldsFromJiraOpen(true),
                            }),
                            ...(editingConfig
                              ? [
                                  { type: 'divider' as const },
                                  autoFillItem('copy', <CopyOutlined />, 'Скопировать из другого типа…', 'Взять готовый маппинг другого типа', {
                                    onClick: () => { setCopySourceDirection('push'); setCopyModalOpen({ direction: 'push' }); },
                                  }),
                                ]
                              : []),
                          ],
                        }}
                      >
                        <Button size="small" loading={aiSuggesting}>
                          Заполнить автоматически <DownOutlined />
                        </Button>
                      </Dropdown>
                    </Space>
                  </div>

                  {/* Закреплённая системная строка summary — выглядит как первая
                      строка таблицы маппинга, но управляется из form.summarySource. */}
                  <div
                    className="jira-summary-row"
                    style={{
                      border: `1px solid ${'var(--color-info)'}`,
                      background: 'var(--color-info-bg)',
                      borderRadius: radius.sm,
                      padding: '8px 10px',
                      marginBottom: spacing.sm,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <Tag color="purple" style={{ marginRight: 0 }}>system</Tag>
                    <div style={{ flex: '0 0 180px' }}>
                      <Typography.Text strong style={{ fontSize: 13 }}>
                        summary <span style={{ color: 'var(--color-error)' }}>*</span>
                      </Typography.Text>
                      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                        заголовок задачи Jira
                      </div>
                    </div>
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>←</span>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      {selectedTypeFields.length > 0 ? (
                        <Select
                          size="small"
                          style={{ width: '100%' }}
                          popupMatchSelectWidth={false}
                          placeholder="Выберите поле Lens"
                          value={(watchedSummarySource as string) || undefined}
                          onChange={(v) => form.setFieldsValue({ summarySource: v ?? '' })}
                          status={!watchedSummarySource ? 'error' : undefined}
                          options={selectedTypeFields.map((f) => ({
                            value: f.slug,
                            label: `${f.name} (${f.slug})`,
                          }))}
                        />
                      ) : (
                        <Input
                          size="small"
                          placeholder="title"
                          value={(watchedSummarySource as string) || ''}
                          onChange={(e) => form.setFieldsValue({ summarySource: e.target.value })}
                        />
                      )}
                    </div>
                    <Tooltip title="Системное поле — нельзя удалить">
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>не удаляется</Typography.Text>
                    </Tooltip>
                  </div>

                  {fieldsLoading || jiraFieldsLoading ? (
                    <Spin size="small" />
                  ) : fieldMappings.length === 0 ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="Нажмите «Добавить» или примените шаблон"
                      style={{ margin: '8px 0' }}
                    />
                  ) : (
                    <Table<FieldMappingRow & { _index: number }>
                      rowKey="_index"
                      dataSource={fieldMappings.map((row, i) => ({ ...row, _index: i }))}
                      pagination={false}
                      size="small"
                      bordered
                      columns={[
                        {
                          title: 'Источник значения',
                          dataIndex: 'activitiesFieldSlug',
                          width: 230,
                          render: (_val: string, record, index: number) => renderLensFieldCell(record, index),
                        },
                        {
                          title: 'Поле Jira',
                          dataIndex: 'jiraField',
                          width: 200,
                          render: (val: string, _, index: number) => renderJiraFieldSelect(val, index),
                        },
                        {
                          title: 'Преобразование',
                          dataIndex: 'transform',
                          width: 200,
                          render: (_val: string, record, index: number) => {
                            if (record.source === 'fixed') {
                              return <Typography.Text type="secondary" style={{ fontSize: 12 }}>—</Typography.Text>;
                            }
                            if (record.jiraField === 'status') {
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                                  <Tag color="gold" style={{ fontSize: 11, margin: 0 }}>workflow</Tag>
                                  <Button
                                    size="small"
                                    onClick={() => openStatusMappingModal('push', index)}
                                    disabled={!record.activitiesFieldSlug || jiraStatusesLoading}
                                  >
                                    Сопоставить статусы
                                  </Button>
                                </div>
                              );
                            }
                            const fd = selectedTypeFields.find((f) => f.slug === record.activitiesFieldSlug);
                            if (fd?.fieldType === 'reference') {
                              const dictId = (fd.config as { dictionaryId?: string } | undefined)?.dictionaryId;
                              const cols = dictId ? refDictColumns[dictId] : undefined;
                              const selectedCol = (cols ?? []).find((c) => c.slug === record.referenceColumnSlug);
                              const colIsBool = selectedCol?.columnType === 'boolean';
                              const vm = record.valueMap ?? {};
                              const boolSummary = vm.true || vm.false
                                ? `Да→${vm.true || '—'} / Нет→${vm.false || '—'}`
                                : null;
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <Tooltip title="reference: какое значение справочника отправить. «Ключевая колонка» — ключ самой записи (логин/код/название). Колонка-ссылка (↗) дорезолвится до ключевой колонки целевого справочника. Колонка Да/Нет требует сопоставления значений.">
                                    <Select<string>
                                      size="small"
                                      style={{ width: '100%' }}
                                      popupMatchSelectWidth={false}
                                      listHeight={320}
                                      value={record.referenceColumnSlug ?? ''}
                                      onChange={(v) =>
                                        updateFieldMapping(index, { referenceColumnSlug: v || undefined })
                                      }
                                      options={[
                                        { value: '', label: '↳ ключевая колонка' },
                                        ...(cols ?? []).map((c) => {
                                          const targetId = (c.config as { dictionaryId?: string } | undefined)?.dictionaryId;
                                          const keyName = targetId ? dictKeyName[targetId] : undefined;
                                          return {
                                            value: c.slug,
                                            label: c.columnType === 'reference'
                                              ? `↗ ${c.name} → ${keyName ?? 'ключ'}`
                                              : c.columnType === 'boolean'
                                                ? `${c.name} (Да/Нет)`
                                                : c.name,
                                          };
                                        }),
                                      ]}
                                    />
                                  </Tooltip>
                                  {colIsBool && (
                                    <>
                                      <Button size="small" onClick={() => openBoolMappingModal('push', index)}>
                                        Да/Нет → значения
                                      </Button>
                                      {boolSummary && (
                                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                                          {boolSummary}
                                        </Typography.Text>
                                      )}
                                    </>
                                  )}
                                </div>
                              );
                            }
                            if (fd?.fieldType === 'boolean') {
                              const vm = record.valueMap ?? {};
                              const summary = vm.true || vm.false
                                ? `Да→${vm.true || '—'} / Нет→${vm.false || '—'}`
                                : null;
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <Button
                                    size="small"
                                    onClick={() => openBoolMappingModal('push', index)}
                                    disabled={!record.activitiesFieldSlug}
                                  >
                                    Да/Нет → значения
                                  </Button>
                                  {summary && (
                                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                                      {summary}
                                    </Typography.Text>
                                  )}
                                </div>
                              );
                            }
                            return (
                              <Select<FieldMappingRow['transform']>
                                size="small"
                                style={{ width: '100%' }}
                                value={record.transform}
                                onChange={(v) => updateFieldMapping(index, { transform: v })}
                                options={TRANSFORM_OPTIONS.filter((o) => o.value !== 'reference_key')}
                              />
                            );
                          },
                        },
                        {
                          title: 'Проверка',
                          render: (_, __, index: number) => renderValidationCell(rowValidations, index),
                        },
                        {
                          title: '',
                          width: 40,
                          align: 'center',
                          render: (_, __, index: number) => (
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<MinusCircleOutlined />}
                              onClick={() => removeFieldMapping(index)}
                            />
                          ),
                        },
                      ]}
                    />
                  )}
                  {globalErrors.length > 0 && (
                    <Alert
                      style={{ marginTop: 8 }}
                      showIcon
                      type="warning"
                      message="Предупреждения"
                      description={
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {globalErrors.map((errText) => (
                            <li key={errText}>{errText}</li>
                          ))}
                        </ul>
                      }
                    />
                  )}

                  {/* Проверка текущего маппинга (dry-run по реальной записи).
                      Запуск выгрузки вынесен в отдельное окно «Синхронизация». */}
                  <Space style={{ marginTop: 12 }} wrap>
                    <Button
                      icon={<EyeOutlined />}
                      onClick={() => {
                        if (previewOpen) {
                          setPreviewOpen(false);
                        } else {
                          handlePushPreview();
                        }
                      }}
                      loading={previewLoading}
                    >
                      {previewOpen ? 'Скрыть предпросмотр' : 'Проверить маппинг'}
                    </Button>
                  </Space>

                  {previewOpen && (
                    <div
                      style={{
                        marginTop: spacing.base,
                        padding: spacing.md,
                        border: `1px solid var(--color-border)`,
                        borderRadius: radius.sm,
                        background: 'var(--color-bg-layout)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <Space size={8}>
                          <Typography.Text strong style={{ fontSize: 13 }}>Предпросмотр push</Typography.Text>
                          {previewLoading && <Spin size="small" />}
                        </Space>
                        <Space size={8}>
                          <Button size="small" onClick={handlePushPreview} loading={previewLoading}>
                            Обновить
                          </Button>
                          <Button size="small" type="text" onClick={() => setPreviewOpen(false)}>
                            Скрыть
                          </Button>
                        </Space>
                      </div>
                      {previewLoading && !previewData ? (
                        <div style={{ textAlign: 'center', padding: 32 }}><Spin size="large" /></div>
                      ) : previewData ? (
                        <div>
                          <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
                            <Descriptions.Item label="Запись">{previewData.itemTitle}</Descriptions.Item>
                            <Descriptions.Item label="Статус">
                              {previewData.canPush
                                ? <Tag color="success">Можно отправить</Tag>
                                : <Tag color="error">{previewData.errors} ошибок</Tag>
                              }
                              {previewData.warnings > 0 && <Tag color="warning">{previewData.warnings} предупр.</Tag>}
                            </Descriptions.Item>
                          </Descriptions>

                          <Typography.Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
                            Поля ({previewData.fields.length})
                          </Typography.Text>
                          {previewData.fields.map(renderPreviewField)}

                          <details style={{ marginTop: 12 }}>
                            <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                              Raw Jira payload (JSON)
                            </summary>
                            <pre style={{ fontSize: 11, background: 'var(--color-bg-layout)', padding: spacing.sm, borderRadius: radius.sm, maxHeight: 200, overflow: 'auto' }}>
                              {JSON.stringify(previewData.jiraPayload, null, 2)}
                            </pre>
                          </details>
                        </div>
                      ) : (
                        <Empty description="Нет данных для предпросмотра" />
                      )}
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: 'pull',
              label: (
                <span>
                  Загрузка (Pull)
                  {pullHasErrors && (
                    <CloseCircleOutlined style={{ color: 'var(--color-error)', fontSize: 13, marginLeft: 6 }} />
                  )}
                </span>
              ),
              children: (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <Typography.Text strong style={{ fontSize: 13 }}>Маппинг Jira → Lens</Typography.Text>
                    <Space size="small">
                      <Button size="small" type="primary" ghost onClick={addPullMappingRow} icon={<PlusOutlined />}>
                        Добавить поле
                      </Button>
                      <Dropdown
                        trigger={['click']}
                        menu={{
                          style: { width: 320, padding: 6, borderRadius: 12 },
                          items: [
                            autoFillItem('lensy', <AiSparkIcon />, 'Заполнить через Lensy', 'ИИ подберёт соответствия полей', {
                              disabled: mappingPrereqMissing !== null,
                              onClick: handlePullAiSuggest,
                            }),
                            autoFillItem('fill-from-push', <SyncOutlined />, 'Заполнить из Push', 'Перенести строки со вкладки «Выгрузка»', {
                              disabled: fieldMappings.length === 0,
                              onClick: handleFillPullFromPush,
                            }),
                            ...(editingConfig
                              ? [
                                  { type: 'divider' as const },
                                  autoFillItem('copy', <CopyOutlined />, 'Скопировать из другого типа…', 'Взять готовый маппинг другого типа', {
                                    onClick: () => { setCopySourceDirection('pull'); setCopyModalOpen({ direction: 'pull' }); },
                                  }),
                                ]
                              : []),
                          ],
                        }}
                      >
                        <Button size="small" loading={aiSuggesting}>
                          Заполнить автоматически <DownOutlined />
                        </Button>
                      </Dropdown>
                    </Space>
                  </div>

                  {pullMappings.length === 0 ? (
                    <Alert
                      type="info"
                      showIcon
                      message="Pull-маппинг не задан — используется автоконверсия из Push"
                      description="Загрузка из Jira работает: значения каждого Jira-поля приводятся к типу соответствующего поля Lens автоматически. Задайте явный маппинг, только если нужно переопределить поведение по умолчанию (например, другую трансформацию или другое целевое поле)."
                      style={{ margin: '8px 0' }}
                    />
                  ) : (
                    <Table<PullMappingRow & { _index: number }>
                      rowKey="_index"
                      dataSource={pullMappings.map((r, i) => ({ ...r, _index: i }))}
                      pagination={false}
                      size="small"
                      bordered
                      columns={[
                        {
                          title: 'Поле Lens',
                          dataIndex: 'activitiesFieldSlug',
                          width: 230,
                          render: (val: string, _r, i: number) =>
                            selectedTypeFields.length > 0 ? (
                              <Select
                                size="small"
                                style={{ width: '100%' }}
                                popupMatchSelectWidth={false}
                                placeholder="Выберите"
                                value={val || undefined}
                                onChange={(v) => updatePullMapping(i, { activitiesFieldSlug: v })}
                                showSearch
                                optionFilterProp="label"
                                options={getLensFieldOptionsForJiraField(pullMappings[i]?.jiraField ?? '')
                                  .filter(
                                    (field) =>
                                      field.value === val ||
                                      !pullMappings.some(
                                        (m, j) => j !== i && m.activitiesFieldSlug === field.value,
                                      ),
                                  )
                                  .map((field) => ({ value: field.value, label: field.label }))}
                              />
                            ) : (
                              <Input
                                size="small"
                                value={val}
                                placeholder="slug"
                                onChange={(e) => updatePullMapping(i, { activitiesFieldSlug: e.target.value })}
                              />
                            ),
                        },
                        {
                          title: 'Поле Jira',
                          dataIndex: 'jiraField',
                          width: 200,
                          render: (val: string, _r, i: number) =>
                            jiraFields.length > 0 ? (
                              <Select
                                size="small"
                                style={{ width: '100%' }}
                                popupMatchSelectWidth={false}
                                placeholder="Выберите"
                                value={val || undefined}
                                onChange={(v) => updatePullMapping(i, { jiraField: v })}
                                showSearch
                                optionFilterProp="label"
                                options={jiraFields
                                  .filter(
                                    (f) =>
                                      f.key === val ||
                                      (!EXCLUDED_JIRA_FIELDS.has(f.key) &&
                                        !pullMappings.some(
                                          (m, j) => j !== i && m.jiraField === f.key,
                                        )),
                                  )
                                  .map((f) => ({ value: f.key, label: f.name }))}
                              />
                            ) : (
                              <Input
                                size="small"
                                value={val}
                                placeholder="assignee, customfield_..."
                                onChange={(e) => updatePullMapping(i, { jiraField: e.target.value })}
                              />
                            ),
                        },
                        {
                          title: 'Преобразование',
                          dataIndex: 'transform',
                          width: 200,
                          render: (val: string, record, i: number) => record.jiraField === 'status' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                              <Tag color="gold" style={{ fontSize: 11, margin: 0 }}>workflow</Tag>
                              <Button
                                size="small"
                                onClick={() => openStatusMappingModal('pull', i)}
                                disabled={!record.activitiesFieldSlug || jiraStatusesLoading}
                              >
                                Сопоставить статусы
                              </Button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <Select<PullMappingRow['transform']>
                                size="small"
                                style={{ width: '100%' }}
                                value={val as PullMappingRow['transform']}
                                onChange={(v) => updatePullMapping(i, { transform: v })}
                                options={PULL_TRANSFORM_OPTIONS}
                              />
                              {record.transform === 'bool_map_reverse' && (
                                <Button
                                  size="small"
                                  onClick={() => openBoolMappingModal('pull', i)}
                                  disabled={!record.activitiesFieldSlug}
                                >
                                  Да/Нет → значения
                                </Button>
                              )}
                            </div>
                          ),
                        },
                        {
                          title: 'Проверка',
                          render: (_, __, i: number) => renderValidationCell(pullRowValidations, i),
                        },
                        {
                          title: '',
                          width: 40,
                          align: 'center',
                          render: (_, __, i: number) => (
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<MinusCircleOutlined />}
                              onClick={() => removePullMapping(i)}
                            />
                          ),
                        },
                      ]}
                    />
                  )}
                  {/* Запуск загрузки и предпросмотр (JQL/режим/max) вынесены в
                      отдельное окно «Синхронизация» — кнопка ↻ в списке маппингов. */}
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginTop: 12 }}
                    message="Запуск загрузки — в окне «Синхронизация»"
                    description="Здесь настраивается только маппинг. Чтобы задать JQL и загрузить задачи из Jira, сохраните маппинг и нажмите ↻ в строке списка."
                  />
                </div>
              ),
            },
          ]}
        />

      </Modal>

      {/* ─── Run-sync modal (запуск синхронизации по сохранённому конфигу) ── */}
      <Modal
        title={
          <Space size={8}>
            <SyncOutlined style={{ color: 'var(--color-info)' }} />
            <span>
              Синхронизация
              {runConfig && (
                <Typography.Text type="secondary" style={{ fontWeight: 400, marginLeft: 8 }}>
                  {itemTypes.find((t) => t.id === runConfig.itemTypeId)?.name ?? runConfig.itemTypeId}
                  {' · '}
                  <Tag color="blue" style={{ margin: 0 }}>{runConfig.jiraProjectKey}</Tag>
                  {' '}{runConfig.jiraIssueType}
                </Typography.Text>
              )}
            </span>
          </Space>
        }
        open={runConfig !== null}
        onCancel={() => setRunConfig(null)}
        footer={[<Button key="close" onClick={() => setRunConfig(null)}>Закрыть</Button>]}
        destroyOnClose
        width={620}
      >
        {/* Выгрузка (Lens → Jira) */}
        <Card
          size="small"
          title={
            <Space size={8}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: radius.md,
                  background: 'var(--color-primary-bg)',
                  color: 'var(--color-primary)',
                }}
              >
                <CloudUploadOutlined />
              </span>
              <span>Выгрузка в Jira</span>
            </Space>
          }
          style={{ marginBottom: spacing.base, borderRadius: radius.lg, boxShadow: shadows.card }}
          styles={{ body: { padding: spacing.md } }}
        >
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
            Все записи этого типа отправляются в Jira по сохранённому маппингу. Новые задачи создаются, существующие обновляются.
          </Typography.Paragraph>
          <Space wrap>
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={() => runConfig && handlePushExecute(runConfig)}
              loading={runConfig ? pushExecuteLoading === runConfig.itemTypeId : false}
            >
              Выгрузить все записи
            </Button>
            <Tooltip title="Тип связи Jira берётся из правила связи (по умолчанию Relates)">
              <Button
                icon={<CloudUploadOutlined />}
                loading={pushLinksRunning}
                onClick={() => handlePushAllJiraLinks(runConfig)}
              >
                Обновить связи в Jira
              </Button>
            </Tooltip>
          </Space>
        </Card>

        {/* Загрузка (Jira → Lens) */}
        <Card
          size="small"
          title={
            <Space size={8}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: radius.md,
                  background: 'var(--color-primary-bg)',
                  color: 'var(--color-primary)',
                }}
              >
                <CloudDownloadOutlined />
              </span>
              <span>Загрузка из Jira</span>
            </Space>
          }
          style={{ borderRadius: radius.lg, boxShadow: shadows.card }}
          styles={{ body: { padding: spacing.md } }}
        >
          <Form layout="vertical" size="small">
            <Form.Item
              label="JQL"
              tooltip="Какие задачи Jira загрузить в Lens"
              style={{ marginBottom: 10 }}
            >
              <Input.TextArea
                rows={2}
                value={runJql}
                onChange={(e) => setRunJql(e.target.value)}
                placeholder="project = GOALS AND assignee = currentUser()"
              />
            </Form.Item>
            <div style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
              <Form.Item label="Режим" style={{ flex: 1, marginBottom: 0 }}>
                <Select
                  value={runMode}
                  onChange={(v) => setRunMode(v as 'create_only' | 'create_and_update')}
                  options={PULL_MODE_OPTIONS}
                />
              </Form.Item>
              <Form.Item label="Max результатов" style={{ flex: 1, marginBottom: 0 }}>
                <InputNumber
                  value={runMaxResults}
                  min={1}
                  max={1000}
                  onChange={(v) => setRunMaxResults(v ?? 100)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item
                label="Поле для Summary"
                tooltip="Куда положить заголовок Jira-задачи в записи Lens"
                style={{ flex: 1, marginBottom: 0 }}
              >
                {runFields.length > 0 ? (
                  <Select
                    value={runSummaryTarget}
                    onChange={setRunSummaryTarget}
                    showSearch
                    optionFilterProp="label"
                    options={runFields.map((f) => ({ value: f.slug, label: f.name }))}
                  />
                ) : (
                  <Input value={runSummaryTarget} onChange={(e) => setRunSummaryTarget(e.target.value)} />
                )}
              </Form.Item>
            </div>
          </Form>
          <Space wrap style={{ marginTop: 12 }}>
            <Button
              type="primary"
              icon={<CloudDownloadOutlined />}
              onClick={handleRunPullExecute}
              loading={pullExecuteLoading}
            >
              Запустить загрузку
            </Button>
            <Button
              icon={<EyeOutlined />}
              onClick={handleRunPullPreview}
              loading={pullPreviewLoading}
            >
              Предпросмотр
            </Button>
          </Space>
        </Card>
      </Modal>

      {/* ─── Pull preview / result modal ──────────────────────────── */}
      <Modal
        title={pullExecuteResult ? 'Результат загрузки из Jira' : 'Предпросмотр загрузки из Jira'}
        open={pullPreviewOpen}
        onCancel={() => { setPullPreviewOpen(false); setPullPreviewData(null); setPullExecuteResult(null); }}
        footer={<Button onClick={() => { setPullPreviewOpen(false); setPullPreviewData(null); setPullExecuteResult(null); }}>Закрыть</Button>}
        width="min(1200px, 90vw)"
      >
        {pullPreviewLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}><Spin size="large" /></div>
        ) : pullExecuteResult ? (
          <div>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Создано">{pullExecuteResult.created}</Descriptions.Item>
              <Descriptions.Item label="Обновлено">{pullExecuteResult.updated}</Descriptions.Item>
              <Descriptions.Item label="Пропущено">{pullExecuteResult.skipped}</Descriptions.Item>
              <Descriptions.Item label="Ошибок">{pullExecuteResult.errors.length}</Descriptions.Item>
            </Descriptions>
            {pullExecuteResult.errors.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Typography.Text strong>Ошибки:</Typography.Text>
                <ul style={{ marginTop: 4 }}>
                  {pullExecuteResult.errors.map((e) => (
                    <li key={e.jiraKey}><Tag>{e.jiraKey}</Tag> {e.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : pullPreviewData ? (
          <div>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Всего issues">{pullPreviewData.total}</Descriptions.Item>
              <Descriptions.Item label="Создать">{pullPreviewData.stats.create}</Descriptions.Item>
              <Descriptions.Item label="Обновить">{pullPreviewData.stats.update}</Descriptions.Item>
              <Descriptions.Item label="Без изменений">{pullPreviewData.stats.noChanges}</Descriptions.Item>
              <Descriptions.Item label="Пропустить">{pullPreviewData.stats.skip}</Descriptions.Item>
            </Descriptions>
            <Table
              size="small"
              style={{ marginTop: 12 }}
              tableLayout="fixed"
              dataSource={pullPreviewData.items}
              rowKey="jiraKey"
              pagination={{ pageSize: 10 }}
              columns={[
                {
                  title: 'Issue',
                  dataIndex: 'jiraKey',
                  width: 180,
                  render: (k: string) => <Tag style={{ marginRight: 0 }}>{k}</Tag>,
                },
                { title: 'Summary', dataIndex: 'summary', ellipsis: true },
                { title: 'Действие', dataIndex: 'action', width: 110 },
              ]}
            />
            {pullPreviewData.skippedFields && pullPreviewData.skippedFields.length > 0 && (
              <Alert
                style={{ marginTop: 8 }}
                type="warning"
                showIcon
                message={`Пропущенных полей: ${pullPreviewData.skippedFields.length}`}
                description={
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {pullPreviewData.skippedFields.slice(0, 5).map((f, i) => (
                      <li key={i}>{f.jiraField} ({f.reason}): {f.message}</li>
                    ))}
                  </ul>
                }
              />
            )}
            {pullPreviewData.samplePreview && pullPreviewData.samplePreview.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Typography.Text strong>Как заполнятся поля Lens (первые задачи):</Typography.Text>
                {pullPreviewData.samplePreview.map((s) => (
                  <div key={s.jiraKey} style={{ marginTop: 8 }}>
                    <Tag>{s.jiraKey}</Tag>
                    <Typography.Text type="secondary"> {s.summary}</Typography.Text>
                    <Table
                      size="small"
                      style={{ marginTop: 6 }}
                      pagination={false}
                      tableLayout="fixed"
                      dataSource={s.fields.map((f, i) => ({ ...f, _i: i }))}
                      rowKey="_i"
                      columns={[
                        {
                          title: 'Поле Lens',
                          dataIndex: 'activitiesFieldName',
                          width: 200,
                          render: (n: string | null, row: any) => (
                            <span>
                              {n ?? row.activitiesFieldSlug}
                              {row.activitiesFieldType ? (
                                <Typography.Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
                                  ({row.activitiesFieldType})
                                </Typography.Text>
                              ) : null}
                            </span>
                          ),
                        },
                        { title: 'Jira-поле', dataIndex: 'jiraField', width: 180 },
                        {
                          title: 'Значение в Jira',
                          dataIndex: 'jiraValue',
                          ellipsis: true,
                          render: (v: unknown) => formatPreviewValue(v),
                        },
                        {
                          title: 'Попадёт в Lens',
                          dataIndex: 'resolvedValue',
                          ellipsis: true,
                          render: (v: string | null, row: any) => {
                            if (row.status === 'ok') return <Tag color="green">{v ?? '(пусто)'}</Tag>;
                            if (row.status === 'skipped') return <Tooltip title={row.message}><Tag color="orange">пропущено</Tag></Tooltip>;
                            return <Tooltip title={row.message}><Tag color="red">ошибка</Tag></Tooltip>;
                          },
                          width: 200,
                        },
                      ]}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <Empty description="Нет данных" />
        )}
      </Modal>

      <Modal
        title="Сопоставление статусов"
        open={!!statusMappingEditor}
        onCancel={() => setStatusMappingEditor(null)}
        onOk={saveStatusMappingModal}
        okText="Сохранить"
        cancelText="Отмена"
        width={860}
        zIndex={1100}
        // Окно длинное (диаграмма workflow), а под ним видна «Сохранить» основной
        // формы: клик по ней попадал в фон и молча отменял правку. Поэтому
        // тело прокручивается, своя «Сохранить» всегда на экране, фон не закрывает.
        maskClosable={false}
        styles={{ body: { maxHeight: 'calc(100vh - 240px)', overflowY: 'auto' } }}
      >
        {!statusMappingRow || !statusMappingLensField || !isLensStatusField(statusMappingLensField) ? (
          <Alert
            type="warning"
            showIcon
            message="Сначала выберите поле Lens типа select со статусными категориями"
          />
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Для каждой опции Lens выберите соответствующий workflow-статус Jira.
              </Typography.Paragraph>
              <Tooltip title="Подобрать совпадения по имени и категории статуса">
                <Button
                  size="small"
                  icon={<AiSparkIcon />}
                  onClick={() => suggestStatusMappingWithLensy()}
                  disabled={jiraStatuses.length === 0 || statusMappingOptions.length === 0}
                >
                  Подсказать Lensy
                </Button>
              </Tooltip>
            </div>
            {statusMappingOptions.map((option) => {
              const categoryMeta = option.category
                ? STATUS_CATEGORY_META[option.category as keyof typeof STATUS_CATEGORY_META]
                : undefined;

              return (
                <div
                  key={option.value}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(180px, 1fr) minmax(220px, 1fr)',
                    gap: 12,
                    alignItems: 'center',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Typography.Text strong>{option.label}</Typography.Text>
                    {categoryMeta && (
                      <Tag color={categoryMeta.color} style={{ margin: 0 }}>
                        {categoryMeta.label}
                      </Tag>
                    )}
                  </div>
                  <Select
                    allowClear
                    loading={jiraStatusesLoading}
                    placeholder="Выберите Jira-статус"
                    value={statusMappingEditor?.valueMap[option.value]}
                    onChange={(value) => {
                      setStatusMappingEditor((current) => {
                        if (!current) return current;
                        const nextValueMap = { ...current.valueMap };
                        if (!value) delete nextValueMap[option.value];
                        else nextValueMap[option.value] = value;
                        return { ...current, valueMap: nextValueMap };
                      });
                    }}
                    options={jiraStatuses.map((status) => ({
                      value: status.name,
                      label: status.name,
                    }))}
                  />
                </div>
              );
            })}

            {statusMappingEditor?.mode === 'push' && (
              editingConfig ? (
                <>
                  <Divider style={{ margin: '4px 0' }}>Диаграмма переходов workflow</Divider>
                  <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    При выгрузке Lens сам построит путь до целевого статуса, даже если workflow
                    не позволяет перейти за один шаг. Граф копится из реальных переходов Jira —
                    нажмите «Сканировать workflow», и Lens возьмёт по задаче на каждый статус
                    проекта (read-only), чтобы собрать полную картину.
                  </Typography.Paragraph>
                  <Button
                    type="primary"
                    block
                    onClick={() => void handleScanAllTransitionGraph()}
                    loading={transitionScanAllLoading}
                  >
                    Сканировать workflow
                  </Button>
                  <Collapse
                    ghost
                    size="small"
                    items={[
                      {
                        key: 'manual',
                        label: 'Сканировать по конкретным задачам',
                        children: (
                          <Space.Compact style={{ width: '100%' }}>
                            <Input
                              placeholder="Ключи задач через запятую: EKF12-37, EKF12-40"
                              value={transitionScanKeys}
                              onChange={(e) => setTransitionScanKeys(e.target.value)}
                              onPressEnter={() => void handleScanTransitionGraph()}
                            />
                            <Button
                              onClick={() => void handleScanTransitionGraph()}
                              loading={transitionScanLoading}
                            >
                              Сканировать
                            </Button>
                          </Space.Compact>
                        ),
                      },
                    ]}
                  />
                  {transitionGraphLoading ? (
                    <Skeleton active paragraph={{ rows: 3 }} />
                  ) : transitionGraph ? (
                    <TransitionGraphDiagram
                      graph={transitionGraph}
                      mappedTargets={Object.values(statusMappingEditor.valueMap)}
                    />
                  ) : null}
                </>
              ) : (
                <Alert
                  type="info"
                  showIcon
                  message="Сохраните маппинг, затем откройте его снова — появится диаграмма переходов workflow и кнопка сканирования."
                />
              )
            )}
          </Space>
        )}
      </Modal>

      {/* ─── Bool value mapping modal (Да/Нет → значение Jira) ─────── */}
      <Modal
        title="Сопоставление значений Да/Нет"
        open={!!boolMappingEditor}
        onCancel={() => setBoolMappingEditor(null)}
        onOk={saveBoolMappingModal}
        okText="Сохранить"
        cancelText="Отмена"
        zIndex={1100}
        maskClosable={false}
      >
        <Typography.Paragraph type="secondary">
          Укажите, какое значение Jira-поля отправлять для «Да» и «Нет».
          {boolAllowedValues.length > 0
            ? ' Значения подставлены из списка допустимых значений Jira.'
            : ' Введите точное значение опции Jira (например «Штат» / «Вендор»).'}
        </Typography.Paragraph>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {([
            { key: 'true', label: 'Да (true)' },
            { key: 'false', label: 'Нет (false)' },
          ] as const).map(({ key, label }) => {
            const onChange = (v: string | undefined) =>
              setBoolMappingEditor((cur) => {
                if (!cur) return cur;
                const next = { ...cur.valueMap };
                if (!v) delete next[key];
                else next[key] = v;
                return { ...cur, valueMap: next };
              });
            return (
              <div
                key={key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(120px, 1fr) minmax(220px, 1.5fr)',
                  gap: 12,
                  alignItems: 'center',
                }}
              >
                <Typography.Text strong>{label}</Typography.Text>
                {boolAllowedValues.length > 0 ? (
                  <Select
                    allowClear
                    showSearch
                    placeholder="Значение Jira"
                    value={boolMappingEditor?.valueMap[key]}
                    onChange={(v) => onChange(v)}
                    options={boolAllowedValues.map((v) => ({ value: v, label: v }))}
                  />
                ) : (
                  <Input
                    allowClear
                    placeholder="Значение Jira-опции"
                    value={boolMappingEditor?.valueMap[key] ?? ''}
                    onChange={(e) => onChange(e.target.value || undefined)}
                  />
                )}
              </div>
            );
          })}
        </Space>
      </Modal>

      {/* ─── Copy mapping modal ───────────────────────────────────── */}
      <Modal
        title={
          copyModalOpen?.direction === 'push'
            ? 'Скопировать push-маппинг'
            : copyModalOpen?.direction === 'pull'
              ? 'Скопировать pull-маппинг'
              : 'Скопировать маппинг'
        }
        open={!!copyModalOpen}
        onCancel={() => { setCopyModalOpen(null); setCopySourceItemTypeId(null); }}
        onOk={handleCopyMapping}
        okText="Скопировать"
        cancelText="Отмена"
        okButtonProps={{ disabled: !copySourceItemTypeId }}
      >
        <Typography.Paragraph type="secondary">
          Выберите тип записи-источник. Существующий маппинг текущего типа будет перезаписан.
        </Typography.Paragraph>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Select
            style={{ width: '100%' }}
            placeholder="Источник"
            value={copySourceItemTypeId ?? undefined}
            onChange={setCopySourceItemTypeId}
            options={configs
              .filter((c) => {
                // Текущий itemType разрешаем только для кросс-направления —
                // тогда копирование push↔pull выполняется локально хелперами.
                if (c.itemTypeId === editingConfig?.itemTypeId) {
                  return (
                    copyModalOpen?.direction !== 'both' &&
                    copyModalOpen?.direction !== copySourceDirection
                  );
                }
                return true;
              })
              .map((c) => {
                const isSelf = c.itemTypeId === editingConfig?.itemTypeId;
                const baseLabel = itemTypes.find((t) => t.id === c.itemTypeId)?.name ?? c.itemTypeId;
                return {
                  value: c.itemTypeId,
                  label: isSelf ? `${baseLabel} (этот тип, кросс-конверсия)` : baseLabel,
                };
              })}
          />
          {copyModalOpen?.direction !== 'both' && (
            <div>
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
                Из какого маппинга источника копировать:
              </Typography.Text>
              <Segmented
                value={copySourceDirection}
                onChange={(v) => setCopySourceDirection(v as 'push' | 'pull')}
                options={[
                  { label: 'Push (выгрузка)', value: 'push' },
                  { label: 'Pull (загрузка)', value: 'pull' },
                ]}
                block
              />
              {copyModalOpen?.direction !== copySourceDirection && (
                <Typography.Text type="warning" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
                  {copyModalOpen?.direction === 'push'
                    ? 'Pull-маппинг будет преобразован в push (трансформации развёрнуты обратно).'
                    : 'Push-маппинг будет преобразован в pull (fixed-строки пропускаются).'}
                </Typography.Text>
              )}
            </div>
          )}
        </Space>
      </Modal>

      {editingConfig && (
        <AddFieldsFromJiraWizard
          spaceId={spaceId}
          itemTypeId={editingConfig.itemTypeId}
          itemTypeName={
            itemTypes.find((t) => t.id === editingConfig.itemTypeId)?.name ?? 'Тип записи'
          }
          open={addFieldsFromJiraOpen}
          onCancel={() => setAddFieldsFromJiraOpen(false)}
          onAdded={async () => {
            setAddFieldsFromJiraOpen(false);
            // Перечитываем список конфигов — fieldMapping мог измениться.
            try {
              const fresh = await listSyncConfigs(spaceId);
              setConfigs(fresh);
              void message.success('Поля добавлены — переоткройте конфиг, чтобы увидеть обновлённый маппинг');
            } catch (e) {
              console.warn('reload after add-fields failed', e);
            }
          }}
        />
      )}
    </Space>
  );
}

function formatPreviewValue(v: unknown): string {
  if (v === null || v === undefined) return '(пусто)';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
